import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AccessibilityControls } from './components/AccessibilityControls';
import {
  A11Y_STORAGE_KEY,
  getInitialA11yScheme,
  getInitialHoverZoomOthers,
  syncHoverZoomOthersToDom,
} from './accessibility/a11yPreferences';
import { db } from './firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { languageManifest as baseLanguageManifest, countryEndonyms } from './locales';
import { useManagedLanguages } from './hooks/useManagedLanguages';
import { en as enMaster } from './locales/en';
import { localizationService } from './services/localizationService';
import { adminServices } from './services/adminServices';
import {
  getAlternativeLevels,
  levelDisplayLabel,
  isRegistrationsPaused,
  REGISTRATION_MODES,
} from './services/pricingRules';
import { OFFER_CODE_TYPES, resolveOfferCodeType, offerCodeTypeLabel } from './constants/offerCodeTypes';
import {
  resolveStep0ContinueAction,
  resolveOfferApplyResult,
  resolveFormSubmitCheckout,
  resolveCheckoutFinalizeAction,
  resolveWebsiteIdentityCheck,
  resolveDuplicateNameCheck,
  resolveSidHydration,
} from './utils/registrationFlowLogic';
import SupportHelpButton from './components/SupportHelpButton';
import SupportIssueModal from './components/SupportIssueModal';

const REG_FLOW_STRINGS = {
  btn_enter_registration: "Enter Registration",
  intro_welcome_title: "Welcome to the IFIP Accreditation Portal",
  intro_guide_p1: "This registration form is the first step for all levels of IFIP global accreditation: Foundation, Certified and Accredited.",
  intro_guide_p2: "If your institution is already registered and you are seeking a renewal or a higher level of accreditation, you do NOT need to complete this form again: you can access your existing portal through your unique link.",
  intro_guide_p3: "If you have lost your access code (Magic Link), you can enter your registered email below and a fresh link will be sent to your inbox.",
  intro_fee_title: "Accreditation Fees",
  intro_guide_fee_box: "During this process, you will have the choice to settle your accreditation fee immediately via card for instant portal access, or request an official invoice for institutional processing.",
  intro_guide_p5: "For general queries regarding the IFIP framework and its global impact, please visit our official website.",
  btn_go_to_recovery: "I already have an account / Lost my link",
  btn_start_new_reg: "Begin New Registration",
  btn_visit_website: "Visit IFIP Website",
  recovery_title: "Account Access Recovery",
  recovery_prompt: "Enter your registered institutional email address to receive your secure access link.",
  recovery_blind_conf: "If an account exists for this email, your unique access instructions have been sent. Please check your inbox and your spam folder.",
  recovery_support_note: "If your institution is already registered but you are a new lead contact, please do not register again. Instead, email info@ifip.group to request a seat transfer. We will require confirmation from the Head of School.",
  btn_back_to_intro: "Back to Intro",
  btn_send_recovery: "Send Magic Link",
  error_duplicate_name: "An institution with this name is already registered in our system. If this is your school, please use the recovery tool or contact info@ifip.group for assistance.",
  workflow_step_duplicate_check: "Checking registry...",
  checkout_title: "Payment Options",
  checkout_desc: "Pay Now or Deferred Access",
  error_bespoke_service: "Your chosen service is not routinely offered in your country yet. Please contact IFIP directly to discuss bespoke options for your organisation:",
  error_bespoke_email: "info@ifip.group", // Changed to accreditation as per system instruction for outbound/system context
  btn_contact_ifip: "Contact IFIP Accreditation Team",
  fee_preselected_note: "Please note that IFIP's fees observe the World Bank's official economic classifications. The correct fee for {{country}} has been pre-selected based on your registration data.",
  card_title: "Pay Now: Immediate Access",
  card_desc:
    "Pay now securely via Stripe (card, Apple Pay, Google Pay, and other methods available in your region) for instant access to the portal.",
  checkout_fee_amount_label: "Your accreditation fee for this application",
  invoice_title: "Request Invoice (Deferred Access)",
  invoice_desc: "We will generate an official Stripe invoice for your institution. Access to the portal is granted once payment has been settled and verified by our finance team.",
  finance_contact_label: "Finance Department Email (Optional)",
  finance_contact_desc: "If you would like a copy of the invoice to be sent directly to your finance or procurement department, please enter their email address here. A copy will also be sent to you.",
  btn_confirm_path: "Confirm Selection and Finish",
  success_paid_title: "Welcome to the IFIP Community",
  success_paid_msg: "Your payment was successful and your registration is complete. You now have full access to the IFIP Accreditation Portal.",
  success_invoice_title: "Registration Received",
  success_invoice_msg: "Your registration has been saved. An official Stripe invoice has been triggered and will be sent to your email address (and your finance contact if provided) shortly.",
  success_link_header: "Your Unique Access Link (Magic Link)",
  success_link_security_note: "This is your unique portal link. A copy has also been sent to your email. You do not need a username or password—this link provides direct and secure access to your institutional audit. Do not share this link with anyone outside your lead team.",
  success_link_warning: "Keep this link safe. It is your private key to the IFIP accreditation workspace.",
  btn_go_to_portal: "Enter My Portal Now",
  card_feature_1: "Instant Portal Access",
  card_feature_2: "Stripe — cards, Apple Pay & Google Pay where available",
  invoice_feature_1: "Official Institutional Invoice",
  invoice_feature_2: "Bank Transfer Support",
  status_connecting_billing: "Connecting to Secure Billing...",
  invoice_success_placeholder: "Awaiting Invoice Settlement",
  success_payment_confirmed: "Your payment has been successful.",
  success_copy_link_label: "Your unique access link (Magic Link):",
  success_copy_btn: "Copy Link",
  success_copied: "Copied!",
  success_portal_guide: "This link will give you access to your portal, allowing you to complete your self-assessment and view your reports.",
  success_email_note: "A copy of the link has been sent to you by email too.",
  success_security_important: "IMPORTANT: ANYONE WITH THIS LINK CAN ENTER YOUR PORTAL. The link will allow immediate access - no password is needed. It is therefore extremely important that you keep your link secure.",
  success_proceed_prompt: "To proceed directly to your portal, please click the button below.",
  success_safe_to_close: "It is now safe to close this page.",
  error_website_required: "The institutional website is a required field.",
  error_exclusions_incomplete:
    "Please enter the total number of exclusion instances for each row and column (use 0 if none).",
  wizard_link_prefix: "Not sure which level to pick?",
  wizard_link_cta: "Try our wizard",
  offer_panel_title: "Have a registration code?",
  offer_panel_desc:
    "Optional. We recognise partner proof-of-purchase codes and checkout discount codes. Discount codes only apply where IFIP collects payment online for your country and level.",
  offer_panel_title_partner: "Enter your proof-of-purchase code",
  offer_panel_hint_partner:
    "This is the receipt code from your partner after you have paid locally — not a discount code.",
  offer_panel_title_discount: "Have a discount code?",
  offer_panel_hint_discount:
    "Optional. The reduced fee is shown at checkout and sent to Stripe (card or invoice). IFIP discount codes are applied here — not Stripe promotion codes.",
  offer_error_need_discount:
    "This code is for partner proof of purchase, not a checkout discount. If you paid through a local partner, choose the level that shows the partner payment step.",
  offer_panel_title_market: "Have a proof-of-purchase code?",
  offer_panel_desc_market:
    "Enter the receipt code from IFIP or your partner to proceed at this level in your country. Discount codes cannot unlock closed markets.",
  offer_code_placeholder: "Enter offer code",
  offer_btn_apply: "Apply code",
  offer_btn_clear: "Remove code",
  offer_applied: "Code applied: {{code}}",
  offer_applied_proof: "Proof of purchase code applied: {{code}}",
  offer_applied_discount: "Discount code applied: {{code}}",
  offer_gateway_note: "You will complete payment at checkout (card or invoice).",
  offer_partner_note:
    "Proof of purchase accepted. Your portal opens immediately after registration. Fees are settled through your partner arrangement; IFIP will confirm payment separately.",
  offer_discount_note:
    "Discount code applied. You will complete payment at checkout (card or invoice) at the offer price.",
  offer_error_invalid: "This code is not valid. Check the code and try again.",
  offer_error_need_proof:
    "This step requires a proof-of-purchase code from your partner — not a discount code. Pay your local partner first, then enter the receipt code they gave you.",
  offer_error_discount_closed_market:
    "Discount codes cannot unlock this country and level. Enter a proof-of-purchase code if IFIP or your partner has issued one.",
  offer_error_not_sure:
    'Offer codes cannot be used with "I\'m not sure yet". Please select Foundation, Certified, or Accredited.',
  step0_country_title: "Where is your institution located?",
  step0_level_title: "Which accreditation level are you applying for?",
  step0_btn_continue: "Continue",
  registrations_paused_title: "Registrations temporarily paused",
  registrations_paused_body:
    "New registrations are paused worldwide. Please contact info@ifip.group for assistance.",
  market_gate_title: "This level is not routinely offered here yet",
  market_gate_body:
    "Foundation accreditation is fully available in {{country}}, but {{levelLabel}} is not yet routinely offered here. It may still be possible — please contact info@ifip.group to discuss first. You can continue with {{alternatives}}, or enter a code below to proceed with {{levelLabel}}.",
  partner_gate_title: "Partner payment required",
  partner_gate_body:
    "{{levelLabel}} accreditation in {{country}} is arranged through {{partnerName}}. Please pay them first, then enter the receipt code from your partner below to continue.",
  partner_gate_body_no_name:
    "{{levelLabel}} accreditation in {{country}} is arranged through a local IFIP partner. Please pay them first, then enter your receipt code below to continue.",
  btn_contact_ifip_discuss: "Contact IFIP to discuss",
  btn_enter_code_continue: "Apply code and continue",
  btn_continue_with_level: "Continue with {{levelLabel}}",
  error_select_country: "Please select your country to continue.",
  error_select_level: "Please select an accreditation level to continue.",
  offer_fee_badge: "Offer price",
  checkout_partner_title: "Review and complete",
  checkout_partner_desc:
    "Your proof-of-purchase code is accepted. Review the details below, then click Complete to finish registration and open your portal. IFIP will confirm partner payment separately.",
  btn_complete_partner_reg: "Complete",
  btn_complete_registration: "Complete",
  status_completing_registration: "Completing registration…",
  error_billing_connection:
    "Billing connection failed. Please try again or contact info@ifip.group",
  resumption_found_title: "Saved Progress Found",
  resumption_found_body: "A registration for this website was previously started but not completed. To protect your data, please click below to receive a secure link to continue your application.",
  btn_send_resumption: "Send Link To Continue Previous Registration",
  resumption_sent_msg: "Resumption link sent! Please check your inbox (and spam folder) to continue.",
  label_accreditation: "Accreditation",
  label_registration: "Registration",
  placeholder_country: "Select Country...",
  placeholder_recovery_email: "email@school.com",
  status_localizing: "Localizing Form...",

  a11y_btn: "Accessibility",
  a11y_panel_title: "Colour scheme",
  a11y_scheme_default: "Default theme",
  a11y_scheme_wb: "White text on black",
  a11y_scheme_cg: "Black text on white",
  a11y_scheme_nb: "Navy on pale blue",
  a11y_scheme_sol: "Teal on cream",
  a11y_panel_aria_split_register: "Accessibility: text size and colour scheme",
  a11y_font_scale_section: "Text size",
  a11y_font_scale_group: "Adjust registration page text size",
  a11y_font_scale_smaller: "Smaller text",
  a11y_font_scale_larger: "Larger text",
  help_btn: "Help",

  level_foundation: "Foundation",
  level_certified: "Certified",
  level_accredited: "Accredited",

  // --- 1. Invoice Pending (Scenario: School chose Invoice at signup) ---
  email1_invoice_pending_subject: "IFIP Accreditation Registration Received: {{schoolName}}",
  email1_invoice_pending_body: "Dear {{orgLead}},\n\nThank you for registering with IFIP. We have received your application data and your official invoice has been generated.\n\nYour access to the IFIP Accreditation Portal is currently pending. As soon as our finance team verifies your invoice payment, you will receive a follow-up email containing your secure Magic Link to begin your self-evaluation.\n\nThank you for your commitment to inclusive education.\n\nIFIP",

  // --- 2. Portal Access (Scenario: Payment confirmed, portal now open) ---
  email2_portal_access_subject: "Your IFIP Portal Access Link",
  email2_portal_access_body: "Dear {{orgLead}},\n\nWe have received your payment for your IFIP Accreditation at {{level}} level.\n\nYour accreditation workspace for {{schoolName}} is now active. You can now access your portal to begin your Self-Evaluation and upload supporting evidence.\nPlease note: This link is your unique security key and does not require a password. Do not share this email with anyone outside your lead team as it provides immediate access to your confidential portal. Please keep this email safe.\n\hank you for your commitment to inclusive education.",
  email2_portal_access_btn: "Enter IFIP Portal",

  // --- 3. Resumption Link (Scenario: User returns to an incomplete form) ---
  email3_resumption_link_subject: "Resume your registration for {{schoolName}}",
  email3_resumption_link_body: "Dear {{orgLead}},\n\nWe found your saved progress for {{schoolName}}. Click the button below to pick up exactly where you left off. This link will allow you to complete your registration and select your payment method.\nIFIP",
  email3_resumption_link_btn: "Resume Registration",

  // --- 4. Fact-Check Release (Scenario: auditor releases draft for review) ---
  email4_factcheck_release_subject: "Action Required: Report Fact-Check for {{schoolName}}",
  email4_factcheck_release_body: "Dear {{orgLead}},\nYour assigned accreditor has completed the review of your Self-Evaluation Form and the supporting data provided. We are pleased to share that a draft of your accreditation report is now ready for your attention.\nWhile the conclusions of the IFIP report are final, we prioritize precision and clarity in our documentation. Before we proceed to the formal release, we invite you to review the attached draft specifically for factual accuracy or any typographical or administrative errors.\n\nOnce you have confirmed the details are correct, we will finalize and issue your official PDF report alongside your digital badge.\n\nThank you for your continued commitment to inclusion.\n\n",
  email4_factcheck_release_btn: "Review Draft Report",

  // --- 5. Final Publication (Scenario: Cycle complete, report & badge issued) ---
  email5_final_publication_subject: "Official Release: IFIP Accreditation for {{schoolName}}",
  email5_final_publication_body: "Dear {{orgLead}},\nFollowing the review of your draft, we are pleased to confirm that your IFIP Accreditation Report is now final.\n\nThe full report, including the specific ratings for each individual station, is now available to view within your accreditation portal. From the portal, you also have the option to generate a PDF version of the report for printing or sharing with your stakeholders as you desire.\n\nIn celebration of this milestone, we have also issued your IFIP Digital Badge. The embed code for the badge can be found directly within your portal; please pass this to your IT lead for inclusion on your website. Please note that this badge is valid for the duration of your current accreditation and will automatically expire when your status is due for review in 48 months.\nYour portal remains open, and we hope you will continue to engage with the IFIP for your ongoing training and professional development needs.\nCongratulations on this achievement, and thank you for your dedication to advancing inclusion.\n\n",
  email5_final_publication_btn: "View Final Report & Digital Badge",

  // --- 6. Admin Action Required (Scenario: Priority Red tasks for IFIP staff) ---
  email6_admin_action_required_subject: "IFIP ACTION REQUIRED: {{label}}",
  email6_admin_action_required_body: "Dear {{adminName}},\n\nYou have been assigned a task in the IFIP Accreditation Portal.\n\nTask: {{label}}\nCategory: {{category}}\n\nPlease log in to the admin dashboard to resolve this item.\n",
  email6_admin_action_required_btn: "Open Action Center",

  // --- 7. Security Alert (Scenario: Mismatched identity attempt) ---
  email7_security_mismatch_subject: "Security Alert: Identity Mismatch for {{schoolName}}",
  email7_security_mismatch_body: "A user attempted to recover the access link for {{schoolName}} using the email address: {{mismatchEmail}}. Our records show this domain belongs to the school, but the specific email is not registered as the Lead Contact. Please verify if a Seat Transfer is required.",
  email7_security_mismatch_btn: "View School Dossier",

  // --- 8. Staff-initiated portal link (Scenario: active school; IFIP resends Magic Link) ---
  email8_admin_portal_resend_subject: "Your IFIP Accreditation Portal link",
  email8_admin_portal_resend_body:
    "Dear {{orgLead}},\nThis message contains your secure link to the IFIP Accreditation Portal for {{schoolName}} ({{level}}). Use the button below to open your workspace — no password is required.\nIf you did not expect this message, please contact info@ifip.group.\nFor security, do not share this link outside your institutional lead team.",
  email8_admin_portal_resend_btn: "Open IFIP Portal",

  // --- 9. Return SEF (Scenario: school requested unlock) ---
  email_sef_return_school_request_subject: "Your IFIP Self-Evaluation Form has been returned",
  email_sef_return_school_request_body:
    "Dear {{orgLead}},\n\nAt your request, your self-evaluation form for {{schoolName}} has been unlocked and returned to draft mode.\n\nYou can now update your narrative responses and supporting evidence in the portal.\n\nBest wishes,\nIFIP",
  email_sef_return_school_request_btn: "Open IFIP Portal",

  // --- 10. Return SEF (Scenario: accreditor suggested revisions) ---
  email_sef_return_accreditor_suggestion_subject: "Action required: IFIP has returned your self-evaluation form",
  email_sef_return_accreditor_suggestion_body:
    "Dear {{orgLead}},\n\nMessage from IFIP. Your self-evaluation form has been unlocked and returned to you because:\n{{ifipMessage}}\n\nBest wishes,\nIFIP",
  email_sef_return_accreditor_suggestion_btn: "Open IFIP Portal",

  // --- School: fact-check comments received ---
  email_school_factcheck_received_subject: "We have received your fact-check comments - {{schoolName}}",
  email_school_factcheck_received_body: "Dear {{orgLead}},\n\nThank you. We have received the fact-check comments you submitted for {{schoolName}}.\n\nYour assigned IFIP accreditor will review your points carefully. If any clarification is needed, we may contact you through the portal or by email. Otherwise, you will hear from us again when the next stage of your accreditation is ready: either an updated draft for further review, or confirmation that your report is being prepared for final release.\n\nYou do not need to resubmit the same comments unless we ask you to.\n\nThank you for helping us keep the record accurate.\n\nIFIP Accreditation Team",
  email_school_factcheck_received_btn: "Open IFIP Portal",

  // --- Staff: new school registered (super admins) ---
  email_staff_school_registered_subject: "New school registered: {{schoolName}}",
  email_staff_school_registered_body: "Dear {{adminName}},\n\n{{schoolName}} has registered for IFIP Accreditation at {{level}} level.\n\nCountry: {{country}}\nLead contact: {{orgLead}} ({{schoolEmail}})\n\nPlease assign a lead accreditor in the Admin Hub when ready, and confirm any finance or partner settlement checks that still apply.\n\nIFIP Accreditation Hub",
  email_staff_school_registered_btn: "Open School Directory",

  // --- Staff: lead accreditor assigned ---
  email_staff_accreditor_assigned_subject: "You have been assigned as lead accreditor for {{schoolName}}",
  email_staff_accreditor_assigned_body: "Dear {{adminName}},\n\n{{assignerName}} has assigned you as the lead accreditor for {{schoolName}}.\n\nAccreditation level: {{level}}\nCountry: {{country}}\n\nPlease open the Admin Hub to review the school record, the SEF (when submitted), and any supporting evidence, then begin your review.\n\nIFIP Accreditation Hub",
  email_staff_accreditor_assigned_btn: "Open school in Registry",

  // --- Staff: SEF submitted ---
  email_staff_sef_submitted_subject: "SEF submitted: {{schoolName}}",
  email_staff_sef_submitted_body: "Dear {{adminName}},\n\n{{schoolName}} has submitted their Self-Evaluation Form and supporting evidence for review.\n\nAccreditation level: {{level}}\nLead accreditor: {{accreditorName}}\n\nPlease open the Admin Hub to begin analysis and report drafting. Super admins receive this notice for oversight; the lead accreditor should take forward the review.\n\nIFIP Accreditation Hub",
  email_staff_sef_submitted_btn: "Open school in Registry",

  // --- Staff: fact-check feedback from school ---
  email_staff_factcheck_feedback_subject: "Fact-check feedback received: {{schoolName}}",
  email_staff_factcheck_feedback_body: "Dear {{adminName}},\n\n{{schoolName}} has submitted fact-check comments on their draft accreditation report.\n\nLead accreditor: please review the feedback, amend the draft where needed, then send it to QA.\n\nSenior and super admins: this notice is for oversight. QA release or finalisation follows the usual permissions after the lead accreditor has amended the draft.\n\nIFIP Accreditation Hub",
  email_staff_factcheck_feedback_btn: "Open report",

  // --- Staff: school accepted draft ---
  email_staff_draft_accepted_subject: "School accepted draft: {{schoolName}}",
  email_staff_draft_accepted_body: "Dear {{adminName}},\n\n{{schoolName}} has accepted their draft accreditation report.\n\nThe cycle is ready for finalisation. Staff with publish or peer-review permission can complete final release in the Admin Hub.\n\nLead accreditor: {{accreditorName}}\n\nIFIP Accreditation Hub",
  email_staff_draft_accepted_btn: "Open report",

  // --- Staff: second reviewer assigned ---
  email_staff_second_reviewer_assigned_subject: "You have been assigned as second reviewer for {{schoolName}}",
  email_staff_second_reviewer_assigned_body: "Dear {{adminName}},\n\n{{assignerName}} has assigned you as second reviewer for {{schoolName}}.\n\nLead accreditor: {{accreditorName}}\nAccreditation level: {{level}}\n\nPlease open the report in the Admin Hub, complete peer review / QA, then either release to the school or approve the next step according to the current workflow stage.\n\nIFIP Accreditation Hub",
  email_staff_second_reviewer_assigned_btn: "Open report",

  // --- Staff: report released to school (to lead accreditor) ---
  email_staff_report_released_subject: "Report released to school: {{schoolName}}",
  email_staff_report_released_body: "Dear {{adminName}},\n\n{{reviewerName}} has approved the report for {{schoolName}} and released it to the school for fact-check.\n\nThe school lead contact will be invited to review the draft for factual accuracy. You will be notified if they accept it or submit comments.\n\nIFIP Accreditation Hub",
  email_staff_report_released_btn: "Open school in Registry",

  // --- Staff: changes requested on report (to lead accreditor) ---
  email_staff_changes_requested_subject: "Changes requested on report: {{schoolName}}",
  email_staff_changes_requested_body: "Dear {{adminName}},\n\n{{reviewerName}} has reviewed the report for {{schoolName}} and requested changes before it can move forward.\n\nPlease open the report in the Admin Hub, review the feedback, amend as needed, then resubmit for review.\n\nIFIP Accreditation Hub",
  email_staff_changes_requested_btn: "Open report"
};

const normalizeUrl = (url) => {
  if (!url) return "";
  let domain = url.toLowerCase().trim();
  domain = domain.replace(/^(https?:\/\/)/, "");
  domain = domain.replace(/^www\./, "");
  domain = domain.split(/[/?#]/)[0];
  return domain;
};

/** Deep-link from renewal and elsewhere: `/register?recovery=1` or `?view=recovery`. */
const readRecoveryDeepLink = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get('recovery') === '1' || p.get('recovery') === 'true' || p.get('view') === 'recovery';
  } catch {
    return false;
  }
};

const REGISTER_FONT_SCALE_KEY = 'ifip_register_font_scale_v1';
const APPLIED_OFFER_STORAGE_KEY = 'ifip_reg_applied_offer_v1';

const isProofOffer = (offer) =>
  !!offer && resolveOfferCodeType(offer) === OFFER_CODE_TYPES.PROOF_OF_PURCHASE;

const readStoredAppliedOffer = () => {
  try {
    const raw = sessionStorage.getItem(APPLIED_OFFER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistAppliedOffer = (offer) => {
  try {
    if (offer) sessionStorage.setItem(APPLIED_OFFER_STORAGE_KEY, JSON.stringify(offer));
    else sessionStorage.removeItem(APPLIED_OFFER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const offerFromSchoolDoc = (docOffer) => {
  if (!docOffer?.code) return null;
  return {
    valid: true,
    code: docOffer.code,
    label: docOffer.label || '',
    codeType: docOffer.codeType || 'proof_of_purchase',
    requirePaymentGateway: docOffer.requirePaymentGateway !== false,
    amounts: docOffer.amounts || {},
  };
};

function clampRegisterFontScale(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.min(1.5, Math.max(0.75, Math.round(n * 100) / 100));
}

// --- SUB-COMPONENT: AUTO-EXPANDING TEXTAREA ---
const AutoExpandingTextarea = ({ value, onChange, placeholder, style }) => {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "inherit";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required
      rows={1}
      style={{ ...style, resize: 'none', overflow: 'hidden', minHeight: '45px' }}
    />
  );
};

const Register = () => {
  const { languageManifest } = useManagedLanguages();
  const [langKey, setLangKey] = useState('en');
  const [view, setView] = useState(() => (readRecoveryDeepLink() ? 'RECOVERY' : 'SPLASH')); 
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canProgress, setCanProgress] = useState(false);

  // Recovery & Security States
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoverySent, setRecoverySent] = useState(false);
  const [duplicateFound, setDuplicateFound] = useState(false);
  const [resumptionStatus, setResumptionStatus] = useState(null); // null, 'found', 'sent'
  const [resumedSid, setResumedSid] = useState(null);

  // Payment & Checkout States
  const [paymentChoice, setPaymentChoice] = useState('card');
  const [financeEmail, setFinanceEmail] = useState("");
  const [generatedMagicLink, setGeneratedMagicLink] = useState("");
  const [checkoutFeeDisplay, setCheckoutFeeDisplay] = useState('');
  const [offerCodeDraft, setOfferCodeDraft] = useState('');
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [offerError, setOfferError] = useState('');
  const [offerValidating, setOfferValidating] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [step0Screen, setStep0Screen] = useState('select'); // select | market_gate | partner_gate | paused

  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedMagicLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };
  // END OF ADDED LINES

  const sentinelRef = useRef(null);
  const [activeData, setActiveData] = useState({ 
    ...enMaster, 
    reg_flow: REG_FLOW_STRINGS 
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const [a11yPanelOpen, setA11yPanelOpen] = useState(false);
  const [a11yScheme, setA11yScheme] = useState(getInitialA11yScheme);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [registerFontScale, setRegisterFontScale] = useState(() => {
    try {
      const raw = localStorage.getItem(REGISTER_FONT_SCALE_KEY);
      if (raw == null || raw === '') return 1;
      return clampRegisterFontScale(parseFloat(raw));
    } catch {
      return 1;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(REGISTER_FONT_SCALE_KEY, String(registerFontScale));
    } catch {
      // ignore
    }
    const el = document.documentElement;
    const prev = el.style.fontSize;
    el.style.fontSize = `${100 * registerFontScale}%`;
    return () => {
      el.style.fontSize = prev;
    };
  }, [registerFontScale]);

  useEffect(() => {
    try {
      const persisted = a11yScheme === 'bw' ? 'cg' : a11yScheme;
      localStorage.setItem(
        A11Y_STORAGE_KEY,
        JSON.stringify({ colorScheme: persisted, hoverZoomOthers: getInitialHoverZoomOthers() })
      );
      syncHoverZoomOthersToDom(getInitialHoverZoomOthers());
    } catch {
      // Ignore persistence failures.
    }
    document.documentElement.setAttribute('data-a11y-scheme', a11yScheme === 'bw' ? 'cg' : a11yScheme);
  }, [a11yScheme]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state) {
        if (e.state.view) setView(e.state.view);
        if (typeof e.state.step === 'number') setCurrentStep(e.state.step);
      }
    };
    window.addEventListener('popstate', handlePopState);
    const deep = readRecoveryDeepLink();
    if (deep) setView('RECOVERY');
    window.history.replaceState({ view: deep ? 'RECOVERY' : 'SPLASH', step: 0 }, '');
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const currentState = window.history.state;
    if (currentState?.view !== view || currentState?.step !== currentStep) {
      window.history.pushState({ view, step: currentStep }, '');
    }
  }, [view, currentStep]);

  useEffect(() => {
    const syncRegistrationContent = async () => {
      setIsTranslating(true);
      try {
        // 1. FAST HYDRATION (Try translations collection first for immediate UI update)
        const storedFlow = await localizationService.getStored(langKey, 'reg_flow_v2');
        const storedUI = await localizationService.getStored(langKey, 'app_main');
        const storedForm = await localizationService.getStored(langKey, 'registration_form');
        
        if (storedFlow && storedUI && storedForm) {
          setActiveData({ 
            ui: storedUI, 
            accreditationForm: storedForm, 
            reg_flow: storedFlow,
            stages: storedForm.stages,
            sections: storedForm.sections
          });
        }

        // 2. BACKGROUND SYNC (Using Shared Global IDs)
        const flowChunk = await localizationService.sync(langKey, 'reg_flow_v2', REG_FLOW_STRINGS);
        const uiChunk = await localizationService.sync(langKey, 'app_main', enMaster.ui);
        const formChunk = await localizationService.sync(langKey, 'registration_form', enMaster.accreditationForm);
        
        setActiveData({
          ui: uiChunk,
          accreditationForm: formChunk,
          reg_flow: flowChunk,
          stages: formChunk.stages,
          sections: formChunk.sections
        });
      } catch (err) {
        console.error("Registration Localization Error:", err);
      } finally {
        setIsTranslating(false);
      }
    };

    syncRegistrationContent();
  }, [langKey]);

  // Derived variables for easier code readability
  const t = activeData.accreditationForm;
  const flow = activeData.reg_flow;
  const ui = activeData.ui;
  const sections = activeData.sections || activeData.accreditationForm?.sections || [];
  const currentLang = languageManifest[langKey] || baseLanguageManifest[langKey] || baseLanguageManifest.en;

  const handleCheckAccess = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) return;
    setRecoverySent(true);

    try {
      const { query, where, getDocs, collection, updateDoc, doc } = await import('firebase/firestore');
      const schoolsRef = collection(db, "schools");
      
      // 1. Check for exact email match
      const qEmail = query(schoolsRef, where("answers.officialEmail", "==", recoveryEmail));
      const snapEmail = await getDocs(qEmail);

      if (!snapEmail.empty) {
        await updateDoc(doc(db, "schools", snapEmail.docs[0].id), {
          magicLinkRequested: true,
          signupLanguage: langKey, // Pass current UI language to email engine
          lastLinkRequestAt: serverTimestamp()
        });
      } else {
        // 2. Fallback: Check domain match for institutional awareness
        const domain = recoveryEmail.split('@')[1];
        const qDomain = query(schoolsRef, where("domain", "==", domain));
        const snapDomain = await getDocs(qDomain);
        
        if (!snapDomain.empty) {
          // Internal flag: Domain recognized, but email mismatch.
          // System can trigger a specific notification to info@ifip.group
          await updateDoc(doc(db, "schools", snapDomain.docs[0].id), {
            recoveryMismatchAttempt: recoveryEmail,
            lastLinkRequestAt: serverTimestamp()
          });
        }
      }
    } catch (err) {
      console.error("Recovery logic error:", err);
    }
    // Logic: Always show success message regardless of result to protect privacy
  };

  const checkDuplicateName = async () => {
    const name = formData.answers['officialName'];
    if (!name) return false;

    setDuplicateFound(false);
    try {
      const { query, where, getDocs, collection } = await import('firebase/firestore');
      const q = query(collection(db, "schools"), where("officialName", "==", name));
      const snap = await getDocs(q);
      
      if (!snap.empty) {
        const dup = resolveDuplicateNameCheck({
          matchIds: snap.docs.map((d) => d.id),
          resumedSid,
        });
        if (dup.action === 'block_duplicate') {
          setDuplicateFound(true);
          return true;
        }
      }
      return false;
    } catch (err) {
      return false;
    }
  };

  const generatePortalLink = () => {
    const newSid = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    setGeneratedMagicLink(`${window.location.origin}/portal?sid=${newSid}`);
    return newSid;
  };

  const totalSteps = (sections || []).length + 1;

  const [formData, setFormData] = useState({
    accreditationLevel: 'foundation',
    answers: {},
    domain: ""
  });
  const supportContext = {
    route: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/register',
    actorType: 'registration_user',
    view,
    currentStep,
    language: langKey,
    accreditationLevel: formData.accreditationLevel,
    schoolName: formData.answers?.officialName || null,
    reporterEmail: formData.answers?.officialEmail || formData.answers?.q3 || recoveryEmail || null,
    recoveryEmail: recoveryEmail || null,
  };

  // 1. REHYDRATION LOGIC: Recover form data if returning from a canceled payment
  useEffect(() => {
    const storedOffer = readStoredAppliedOffer();
    if (storedOffer?.code) setAppliedOffer(storedOffer);
  }, []);

  useEffect(() => {
    if (isProofOffer(appliedOffer)) setPaymentChoice('partner');
  }, [appliedOffer]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled') === 'true') {
      const savedData = sessionStorage.getItem('ifip_reg_backup');
      if (savedData) {
        setFormData(JSON.parse(savedData));
        setView('CHECKOUT');
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    const level = new URLSearchParams(window.location.search).get('level');
    if (['foundation', 'certified', 'accredited'].includes(level)) {
      setFormData((prev) => ({ ...prev, accreditationLevel: level }));
      setView('FORM');
      setCurrentStep(0);
    }
  }, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('sid');
    if (sid && !resumedSid) {
      const hydrateSession = async () => {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const snap = await getDoc(doc(db, "schools", sid));
          const hydration = resolveSidHydration({
            exists: snap.exists(),
            status: snap.exists() ? snap.data().status : null,
          });
          if (hydration.action === 'hydrate_form') {
            const d = snap.data();
            setFormData(prev => ({
              ...prev,
              accreditationLevel: d.accreditationLevel,
              answers: d.answers,
              normalizedWebsite: d.normalizedWebsite
            }));
            setResumedSid(sid);
            setResumptionStatus(null);
            setLangKey(d.signupLanguage || 'en');
            const hydratedOffer = offerFromSchoolDoc(d.appliedOffer) || readStoredAppliedOffer();
            if (hydratedOffer?.code) {
              setAppliedOffer(hydratedOffer);
              persistAppliedOffer(hydratedOffer);
            }
            setView('FORM');
            setCurrentStep(hydration.startStep);
          }
        } catch (err) {
          console.error("Hydration Error:", err);
        }
      };
      hydrateSession();
    }
  }, [resumedSid]);

  // 2. PERSISTENCE LOGIC: Save data before leaving for Stripe
  const saveBackup = () => {
    sessionStorage.setItem('ifip_reg_backup', JSON.stringify(formData));
  };

  const isFinalSectionValid = () => {
     if (!sections || sections.length === 0 || currentStep !== totalSteps - 1) return true;
    const finalSection = sections[sections.length - 1];
    if (!finalSection || !finalSection.questions) return true;
    
    const finalQuestions = finalSection.questions;
    return Object.entries(finalQuestions).every(([qKey, qMetadata]) => {
      if (qMetadata.type === 'checkbox') return formData.answers[qKey] === true;
      return true;
    });
  };

  const getDynamicBackground = () => {
    if (view === 'SUCCESS') return '#0f172a'; 
    const colors = {
      SPLASH: 'linear-gradient(135deg, #007c85 0%, #335c81 40%, #7d337d 75%, #d11c7a 100%)',
      INTRO: 'linear-gradient(180deg, #1e2533 0%, #0f172a 100%)',
      RECOVERY: 'linear-gradient(180deg, #1a2a44 0%, #0f172a 100%)',
      FORM: `linear-gradient(180deg, ${['#1e2533', '#212c41', '#243350', '#273b5f', '#2a426d'][currentStep] || '#1e2533'} 0%, #0f172a 100%)`,
      CHECKOUT: 'linear-gradient(180deg, #138a8a 0%, #0f172a 100%)'
    };
    return colors[view] || colors.INTRO;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFinalSectionValid()) return;
    setIsSubmitting(true);

    const isDup = await checkDuplicateName();
    if (isDup) {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const country = formData.answers['officialAddressCountry'];
      const level = formData.accreditationLevel;

      if (appliedOffer && level === 'notSure') {
        alert(flow.offer_error_not_sure || REG_FLOW_STRINGS.offer_error_not_sure);
        setIsSubmitting(false);
        return;
      }

      if (appliedOffer && ['foundation', 'certified', 'accredited'].includes(level)) {
        let line = '';
        try {
          const quote = await adminServices.getAccreditationFeeQuote(country, level, appliedOffer.code);
          line = quote?.displayLine || '';
        } catch (quoteErr) {
          console.warn('Offer fee quote failed', quoteErr);
        }
        if (!line) {
          const amt = Number(appliedOffer.amounts?.[level]);
          line = `£${amt.toLocaleString('en-GB')} (${appliedOffer.code})`;
        }
        setCheckoutFeeDisplay(line);
        const config = pricingConfig || (await adminServices.getPricingConfig());
        if (!pricingConfig) setPricingConfig(config);
        const feeData = adminServices.getCalculatedFee(country, level, config);
        const checkoutRoute = resolveFormSubmitCheckout({
          appliedOffer,
          level,
          feeData,
        });
        if (checkoutRoute.action === 'alert') {
          alert(flow[checkoutRoute.alertKey] || REG_FLOW_STRINGS[checkoutRoute.alertKey]);
          setIsSubmitting(false);
          return;
        }
        setPaymentChoice(checkoutRoute.paymentChoice);
        setView('CHECKOUT');
        setIsSubmitting(false);
        return;
      }

      // Check market availability before showing payment options
      const config = await adminServices.getPricingConfig();
      const feeData = adminServices.getCalculatedFee(country, level, config);

      const checkoutRoute = resolveFormSubmitCheckout({ appliedOffer, level, feeData });
      if (checkoutRoute.action === 'alert') {
        alert(flow[checkoutRoute.alertKey] || REG_FLOW_STRINGS[checkoutRoute.alertKey]);
        setIsSubmitting(false);
        return;
      }
      if (checkoutRoute.needsOfferAtCheckout) {
        alert(flow.offer_error_need_proof || REG_FLOW_STRINGS.offer_error_need_proof);
        setIsSubmitting(false);
        return;
      }

      if (checkoutRoute.paymentChoice === 'bespoke') {
        setPaymentChoice('bespoke');
        setCheckoutFeeDisplay('');
      } else if (checkoutRoute.paymentChoice === 'partner') {
        setPaymentChoice('partner');
        setCheckoutFeeDisplay('');
      } else {
        let line = '';
        try {
          const quote = await adminServices.getAccreditationFeeQuote(country, level);
          line = quote?.displayLine || '';
        } catch (quoteErr) {
          console.warn('Fee quote failed', quoteErr);
        }
        if (!line) {
          line = `£${Number(feeData.amount).toLocaleString('en-GB')}`;
        }
        setCheckoutFeeDisplay(line);
      }
    } catch (err) {
      console.warn("Pricing check failed, defaulting to standard checkout", err);
      setCheckoutFeeDisplay('');
    }

    setView('CHECKOUT');
    setIsSubmitting(false);
  };

  const handleCheckoutFinalize = async () => {
    setIsSubmitting(true);
    const functions = getFunctions();
    // Normalize email lookup to handle legacy q3 keys or officialEmail mapping
    const email = formData.answers['officialEmail'] || formData.answers['q3'] || ""; // Updated logic
    
    try {
      const proofCheckout = isProofOffer(appliedOffer);
      if (paymentChoice === 'partner' && !appliedOffer) {
        alert(flow.offer_error_need_proof || REG_FLOW_STRINGS.offer_error_need_proof);
        return;
      }

      // 1. Generate the ID first so we can link it to Stripe
      const sid = resumedSid || Math.random().toString(36).substring(2, 15);


      // 2. CREATE THE RECORD IN FIRESTORE (Status: Inactive)
      // This ensures the data exists when Stripe sends the "Success" confirmation
      const { doc, setDoc } = await import('firebase/firestore');
      const level = formData.accreditationLevel;
      const country = formData.answers['officialAddressCountry'];
      const offerCode = appliedOffer?.code || '';

      await setDoc(doc(db, "schools", sid), {
            ...formData,
            accreditationLevel: level,
            registeredAccreditationLevel: level,
            officialEmail: email,
            officialName: formData.answers['officialName'],
            normalizedWebsite: formData.normalizedWebsite,
            status: 'inactive',
            paymentStatus:
              paymentChoice === 'partner'
                ? 'awaiting_external_settlement'
                : paymentChoice === 'card'
                  ? 'pending_card'
                  : 'awaiting_invoice',
            signupLanguage: langKey,
            submittedAt: serverTimestamp(),
            deleteAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            ...(appliedOffer
              ? {
                  appliedOffer: {
                    code: appliedOffer.code,
                    label: appliedOffer.label || '',
                    codeType: resolveOfferCodeType(appliedOffer),
                    requirePaymentGateway: resolveOfferCodeType(appliedOffer) === OFFER_CODE_TYPES.DISCOUNT,
                    amounts: appliedOffer.amounts,
                    appliedLevel: level,
                  },
                }
              : {}),
          });

      const origin = typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://accreditation.ifip.group';

      if (proofCheckout || (paymentChoice === 'partner' && appliedOffer)) {
        const { portalUrl } = await adminServices.activateRegistrationWithPartnerOffer({
          schoolId: sid,
          country,
          level,
          offerCode,
        });
        persistAppliedOffer(null);
        setGeneratedMagicLink(portalUrl || `${origin}/portal?sid=${encodeURIComponent(sid)}`);
        setView('SUCCESS');
        return;
      }

      const payload = {
        schoolId: sid,
        country,
        level,
        officialName: formData.answers['officialName'],
        officialEmail: email,
        financeEmail: financeEmail,
        offerCode,
        successUrl: `${origin}/portal?sid=${encodeURIComponent(sid)}&payment=success`,
        cancelUrl: `${origin}/register?canceled=true`
      };

      if (paymentChoice === 'card') {
        saveBackup(); 
        const createCheckout = httpsCallable(functions, 'createStripeCheckout');
        const { data } = await createCheckout(payload);
        window.location.href = data.url; 
      } else {
        const createInvoice = httpsCallable(functions, 'createStripeInvoice');
        await createInvoice(payload);
        setGeneratedMagicLink(flow.invoice_success_placeholder);
        setView('SUCCESS');
      }
    } catch (err) {
      console.error("Payment Bridge Error:", err);
      alert(flow.error_billing_connection);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (view !== 'FORM') return;
    let cancelled = false;
    adminServices
      .getPricingConfig()
      .then((cfg) => {
        if (!cancelled) setPricingConfig(cfg);
      })
      .catch((err) => console.warn('Pricing config load failed', err));
    return () => {
      cancelled = true;
    };
  }, [view]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCanProgress(false);
    if (view === 'FORM' && currentStep === 0 && step0Screen === 'select') {
      const country = formData.answers?.officialAddressCountry;
      const level = formData.accreditationLevel;
      setCanProgress(!!country && !!level && level !== 'notSure');
    }
  }, [currentStep, view, step0Screen, formData.answers?.officialAddressCountry, formData.accreditationLevel]);

  const advanceFromStep0Gate = () => {
    setStep0Screen('select');
    setCurrentStep(1);
  };

  const evaluateStep0Continue = async () => {
    const country = formData.answers?.officialAddressCountry;
    const level = formData.accreditationLevel;
    if (!country) {
      alert(flow.error_select_country || REG_FLOW_STRINGS.error_select_country);
      return;
    }
    if (!level || level === 'notSure') {
      alert(flow.error_select_level || REG_FLOW_STRINGS.error_select_level);
      return;
    }

    let config = pricingConfig;
    if (!config) {
      try {
        config = await adminServices.getPricingConfig();
        setPricingConfig(config);
      } catch {
        config = null;
      }
    }

    const step0 = resolveStep0ContinueAction({
      country,
      level,
      registrationsPaused: isRegistrationsPaused(config),
      appliedOffer,
      feeData: config
        ? adminServices.getCalculatedFee(country, level, config)
        : { available: true, mode: REGISTRATION_MODES.ONLINE, requiresPartnerCode: false },
    });

    if (step0.action === 'alert') {
      alert(flow[step0.alertKey] || REG_FLOW_STRINGS[step0.alertKey]);
      return;
    }
    if (step0.action === 'set_step0_screen') {
      setStep0Screen(step0.screen);
      return;
    }
    if (step0.action === 'advance_to_form') {
      advanceFromStep0Gate();
    }
  };

  const handleApplyOfferAndMaybeContinue = async () => {
    const code = offerCodeDraft.trim();
    if (!code) return;
    setOfferValidating(true);
    setOfferError('');
    try {
      const country = formData.answers?.officialAddressCountry || '';
      const level =
        formData.accreditationLevel && formData.accreditationLevel !== 'notSure'
          ? formData.accreditationLevel
          : '';
      let config = pricingConfig;
      if (!config) {
        try {
          config = await adminServices.getPricingConfig();
          setPricingConfig(config);
        } catch {
          config = null;
        }
      }
      const feeData = config && country && level
        ? adminServices.getCalculatedFee(country, level, config)
        : { available: true, mode: REGISTRATION_MODES.ONLINE, requiresPartnerCode: false };
      const result = await adminServices.validateRegistrationOfferCode(
        code,
        level,
        country,
        resumedSid || ''
      );
      if (!result?.valid) {
        setAppliedOffer(null);
        persistAppliedOffer(null);
        setOfferError(result?.message || flow.offer_error_invalid || REG_FLOW_STRINGS.offer_error_invalid);
        return;
      }
      const apply = resolveOfferApplyResult({ validationResult: result, step0Screen, feeData });
      if (apply.action === 'reject') {
        setAppliedOffer(null);
        persistAppliedOffer(null);
        setOfferError(
          result?.message ||
            flow[apply.errorKey] ||
            REG_FLOW_STRINGS[apply.errorKey] ||
            flow.offer_error_invalid ||
            REG_FLOW_STRINGS.offer_error_invalid
        );
        return;
      }
      setAppliedOffer(result);
      setOfferCodeDraft(apply.code);
      persistAppliedOffer(result);
      if (currentStep === 0 && apply.autoAdvanceToForm) {
        advanceFromStep0Gate();
      }
    } catch (err) {
      setAppliedOffer(null);
      persistAppliedOffer(null);
      setOfferError(err?.message || flow.offer_error_invalid || REG_FLOW_STRINGS.offer_error_invalid);
    } finally {
      setOfferValidating(false);
    }
  };

  const handleClearOffer = () => {
    setAppliedOffer(null);
    setOfferCodeDraft('');
    setOfferError('');
    persistAppliedOffer(null);
  };

  const renderAppliedOfferFeedback = () => {
    if (!appliedOffer) return null;
    const codeType = resolveOfferCodeType(appliedOffer);
    const isProof = codeType === OFFER_CODE_TYPES.PROOF_OF_PURCHASE;
    const template = isProof
      ? flow.offer_applied_proof || REG_FLOW_STRINGS.offer_applied_proof
      : flow.offer_applied_discount || REG_FLOW_STRINGS.offer_applied_discount;
    const note = isProof
      ? flow.offer_partner_note || REG_FLOW_STRINGS.offer_partner_note
      : flow.offer_discount_note || REG_FLOW_STRINGS.offer_discount_note;
    return (
      <p
        style={{
          margin: '12px 0 0 0',
          fontSize: '0.85rem',
          color: isProof ? '#fbbf24' : '#5eead4',
          lineHeight: 1.5,
        }}
      >
        {template.replace(/\{\{code\}\}/g, appliedOffer.code)}
        {' — '}
        {note}
      </p>
    );
  };

  useEffect(() => {
    // Only attach observer if we are in the FORM view and sections have loaded
    if (view !== 'FORM' || !sections || sections.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => { 
        if (entry.isIntersecting) {
          setCanProgress(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [currentStep, view, sections.length]); // Re-run when view changes or data arrives

  const handleInputChange = (qKey, value) => {
    setFormData(prev => ({
      ...prev,
      answers: { ...prev.answers, [qKey]: value }
    }));
  };

  const isExclusionsGridComplete = (qMetadata, data) => {
    if (!qMetadata?.rows?.length || !qMetadata?.columns?.length) return true;
    for (const row of qMetadata.rows) {
      for (const col of qMetadata.columns) {
        const raw = data?.[row.id]?.[col.id];
        if (raw === '' || raw === undefined || raw === null) return false;
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return false;
      }
    }
    return true;
  };

  const handleNextStep = async () => {
    if (currentStep === 0) {
      if (step0Screen === 'select') {
        await evaluateStep0Continue();
        return;
      }
      return;
    }

    if (currentStep === 1) {
      const rawUrl = formData.answers['q2'];
      if (!rawUrl) {
        alert(flow.error_website_required);
        return;
      }

      const normalized = normalizeUrl(rawUrl);
      setFormData(prev => ({ ...prev, normalizedWebsite: normalized }));

      setIsSubmitting(true);
      try {
        const { query, where, getDocs, collection } = await import('firebase/firestore');
        const q = query(collection(db, "schools"), where("normalizedWebsite", "==", normalized));
        const snap = await getDocs(q);

        if (!snap.empty) {
          const existingDoc = snap.docs[0];
          const existing = existingDoc.data();
          const identity = resolveWebsiteIdentityCheck({
            matchedDocId: existingDoc.id,
            matchedStatus: existing.status,
            resumedSid,
          });
          if (identity.action === 'goto_recovery_view') {
            setView('RECOVERY');
            setIsSubmitting(false);
            return;
          }
          if (identity.action === 'show_resumption_prompt') {
            setResumptionStatus('found');
            setIsSubmitting(false);
            return;
          }
        }
      } catch (err) {
        console.error("Identity check error:", err);
      }
      setIsSubmitting(false);
    }

    const activeSection = sections[currentStep - 1];
    if (activeSection?.id === 'exclusions') {
      const exMeta = activeSection.questions?.schoolExclusions;
      if (exMeta && !isExclusionsGridComplete(exMeta, formData.answers?.schoolExclusions)) {
        alert(flow.error_exclusions_incomplete || REG_FLOW_STRINGS.error_exclusions_incomplete);
        return;
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handleRequestResumption = async () => {
    setIsSubmitting(true);
    try {
      const { query, where, getDocs, collection, updateDoc, doc } = await import('firebase/firestore');
      const q = query(collection(db, "schools"), where("normalizedWebsite", "==", formData.normalizedWebsite));
      const snap = await getDocs(q);

      if (!snap.empty) {
        await updateDoc(doc(db, "schools", snap.docs[0].id), {
          magicLinkRequested: true,
          signupLanguage: langKey,
          lastLinkRequestAt: serverTimestamp()
        });
        setResumptionStatus('sent');
      }
    } catch (err) {
      console.error("Resumption request error:", err);
    }
    setIsSubmitting(false);
  };

  const renderInput = (qKey, qMetadata) => {
    const commonStyles = {
      width: '100%', padding: '12px 15px', borderRadius: '8px', background: 'var(--a11y-input-bg)',
      color: 'var(--a11y-input-text)', border: '1px solid var(--a11y-surface-border)', outline: 'none', fontSize: '0.95rem', boxSizing: 'border-box'
    };

    if (qKey === 'officialAddressCountry') {
    return (
      <select
        value={formData.answers[qKey] || ''}
        onChange={(e) => handleInputChange(qKey, e.target.value)}
        required
        style={{ ...commonStyles, color: 'var(--a11y-input-text)' }}
      >
        <option value="" style={{color: 'black'}}>{flow.placeholder_country}</option>
        {countryEndonyms.map(country => (
          <option key={country} value={country} style={{color: 'black'}}>
            {country}
          </option>
        ))}
      </select>
    );
  }

    switch (qMetadata.type) {
      case 'textarea':
        return (
          <AutoExpandingTextarea
            value={formData.answers[qKey] || ''}
            onChange={(e) => handleInputChange(qKey, e.target.value)}
            placeholder={t.reg_ui.expand_box}
            style={commonStyles}
          />
        );
   
   // Inside Register.js, add these cases to the switch(qMetadata.type) block in renderInput():

case 'percentageGrid':
  return (
    <div className="reg-pct-grid">
      {qMetadata.categories.map((cat, i) => {
        // Find the original English key from the master file to ensure a stable lookup
        const masterQuestions = enMaster.accreditationForm.sections[currentStep - 1]?.questions;
        const englishKey = masterQuestions ? masterQuestions[qKey]?.categories?.[i] : null;
        
        // Check for the description using the translated name OR the original English name
        const descriptionText = qMetadata.descriptions ? (qMetadata.descriptions[cat.trim()] || qMetadata.descriptions[englishKey]) : null;

        return (
          <div key={cat} style={{ 
            background: 'var(--reg-inset-bg)', padding: '15px', borderRadius: '8px', 
            border: '1px solid var(--reg-glass-border-subtle)', display: 'flex', 
            flexDirection: 'column', gap: '8px', position: 'relative' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--a11y-page-text)', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>
                {cat}
              </label>
              {descriptionText && (
                <span className="reg-pct-tooltip">
                  <button type="button" className="reg-pct-tooltip__trigger" aria-label={ui?.tooltip_help || 'Help'}>?</button>
                  <div className="reg-pct-tooltip__box" role="tooltip">
                    {descriptionText}
                  </div>
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--reg-inset-bg-strong)', padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(19, 138, 138, 0.2)' }}>
              <input 
                type="number" min="0" max="100" placeholder="0"
                value={formData.answers[qKey]?.[cat] || ''}
                onChange={(e) => handleInputChange(qKey, { ...formData.answers[qKey], [cat]: e.target.value })}
                style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--a11y-page-text)', fontSize: '1.1rem', fontWeight: 'bold', outline: 'none', textAlign: 'right' }}
              />
              <span style={{ color: '#138a8a', fontWeight: '900', fontSize: '1rem' }}>%</span>
            </div>
          </div>
        );
      })}
    </div>
  );

case 'exclusionsGrid': {
        const gridData = formData.answers[qKey] || {};
        const setCell = (rowId, colId, raw) => {
          const cleaned = raw.replace(/[^\d]/g, '');
          handleInputChange(qKey, {
            ...gridData,
            [rowId]: { ...(gridData[rowId] || {}), [colId]: cleaned },
          });
        };
        return (
          <div className="reg-exclusions-grid-wrap">
            <div className="reg-exclusions-grid-scroll">
              <table className="reg-exclusions-grid" role="grid">
                <thead>
                  <tr>
                    <th scope="col" className="reg-exclusions-grid__corner" />
                    {(qMetadata.columns || []).map((col) => (
                      <th key={col.id} scope="col" className="reg-exclusions-grid__col-head">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(qMetadata.rows || []).map((row) => (
                    <tr key={row.id}>
                      <th scope="row" className="reg-exclusions-grid__row-head">
                        <span className="reg-exclusions-grid__row-label">{row.label}</span>
                        {row.guidance ? (
                          <span className="reg-exclusions-grid__row-guidance">{row.guidance}</span>
                        ) : null}
                      </th>
                      {(qMetadata.columns || []).map((col) => (
                        <td key={col.id} className="reg-exclusions-grid__cell">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            required
                            aria-label={`${row.label} — ${col.label}`}
                            placeholder="0"
                            value={gridData[row.id]?.[col.id] ?? ''}
                            onChange={(e) => setCell(row.id, col.id, e.target.value)}
                            className="reg-exclusions-grid__input"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

case 'trainingChecklist':
  return (
    <div style={{ background: 'var(--reg-inset-bg-mid)', padding: '15px', borderRadius: '8px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', color: 'var(--a11y-page-text)' }}>
        {qMetadata.options.map(option => (
          <label key={option} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox"
              checked={formData.answers[qKey]?.includes(option) || false}
              onChange={(e) => {
                const currentValues = formData.answers[qKey] || [];
                const updatedValues = e.target.checked 
                  ? [...currentValues, option] 
                  : currentValues.filter(v => v !== option);
                handleInputChange(qKey, updatedValues);
              }}
              style={{ width: '18px', height: '18px', accentColor: '#138a8a', cursor: 'pointer' }}
            />
            <span style={{ opacity: formData.answers[qKey]?.includes(option) ? 1 : 0.6 }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

case 'specialistCount':
  return (
    <div style={{ background: 'var(--reg-inset-bg-mid)', padding: '15px', borderRadius: '8px' }}>
      <input 
        type="number" 
        placeholder={qMetadata.placeholder || "Total Headcount"}
        value={formData.answers[qKey]?.total || ''}
        onChange={(e) => handleInputChange(qKey, { ...formData.answers[qKey], total: e.target.value })}
        style={{ width: '100%', marginBottom: '15px', background: 'var(--a11y-input-bg)', border: '1px solid var(--a11y-surface-border)', color: 'var(--a11y-input-text)', padding: '10px', boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {qMetadata.roles.map(role => (
          <label key={role} style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox"
              checked={formData.answers[qKey]?.roles?.includes(role) || false}
              onChange={(e) => {
                const currentRoles = formData.answers[qKey]?.roles || [];
                const updatedRoles = e.target.checked ? [...currentRoles, role] : currentRoles.filter(r => r !== role);
                handleInputChange(qKey, { ...formData.answers[qKey], roles: updatedRoles });
              }}
            /> {role}
          </label>
        ))}
      </div>
    </div>
  );   
    
   case 'checkbox':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer', padding: '15px', background: 'var(--reg-glass-bg-soft)', borderRadius: '8px', border: '1px solid var(--reg-glass-border-subtle)' }}>
            <input 
              type="checkbox" 
              checked={formData.answers[qKey] || false}
              onChange={(e) => handleInputChange(qKey, e.target.checked)}
              style={{ width: '20px', height: '20px', accentColor: '#138a8a', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.9rem', color: 'var(--a11y-page-text)', opacity: 0.92 }}>{qMetadata.description}</span>
          </label>
        );
      case 'boolean':
        return (
          <div style={{ display: 'flex', gap: '15px' }}>
            {[
              { label: ui?.btn_yes || 'Yes', value: 'Yes' },
              { label: ui?.btn_no || 'No', value: 'No' }
            ].map((option) => (
              <button
                key={option.value} type="button"
                onClick={() => handleInputChange(qKey, option.value)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', border: '2px solid',
                  borderColor: formData.answers[qKey] === option.value ? '#138a8a' : 'var(--reg-glass-border-subtle)',
                  background: formData.answers[qKey] === option.value ? 'rgba(19, 138, 138, 0.2)' : 'var(--reg-bool-idle-bg)',
                  color: formData.answers[qKey] === option.value ? '#138a8a' : 'var(--a11y-page-text)',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        );
      default:
        return (
          <input
            type={qMetadata.type} required style={commonStyles}
            placeholder={qMetadata.placeholder || ''}
            value={formData.answers[qKey] || ''}
            onChange={(e) => handleInputChange(qKey, e.target.value)}
          />
        );
    }
  };

  const pickRegisterA11yLabels = () => ({
    btn: flow.a11y_btn || ui.a11y_btn || enMaster.ui.a11y_btn,
    panelTitle: flow.a11y_panel_title || ui.a11y_panel_title || enMaster.ui.a11y_panel_title,
    panelAria: flow.a11y_panel_title || ui.a11y_panel_title || enMaster.ui.a11y_panel_title,
    panelAriaSplit:
      flow.a11y_panel_aria_split_register ||
      ui.a11y_panel_aria_split_register ||
      enMaster.ui.a11y_panel_aria_split_register ||
      enMaster.ui.a11y_panel_aria_split ||
      'Accessibility: text size and colour scheme',
    fontScaleSectionTitle:
      flow.a11y_font_scale_section || ui.a11y_font_scale_section || enMaster.ui.a11y_font_scale_section || 'Text size',
    fontScaleGroupAria:
      flow.a11y_font_scale_group || ui.a11y_font_scale_group || enMaster.ui.a11y_font_scale_group || 'Adjust text size',
    fontScaleSmaller:
      flow.a11y_font_scale_smaller || ui.a11y_font_scale_smaller || enMaster.ui.a11y_font_scale_smaller || 'Smaller text',
    fontScaleLarger:
      flow.a11y_font_scale_larger || ui.a11y_font_scale_larger || enMaster.ui.a11y_font_scale_larger || 'Larger text',
    schemeDefault: flow.a11y_scheme_default || ui.a11y_scheme_default || enMaster.ui.a11y_scheme_default,
    schemeWb: flow.a11y_scheme_wb || ui.a11y_scheme_wb || enMaster.ui.a11y_scheme_wb,
    schemeCg: flow.a11y_scheme_cg || ui.a11y_scheme_cg || enMaster.ui.a11y_scheme_cg,
    schemeNb: flow.a11y_scheme_nb || ui.a11y_scheme_nb || enMaster.ui.a11y_scheme_nb,
    schemeSol: flow.a11y_scheme_sol || ui.a11y_scheme_sol || enMaster.ui.a11y_scheme_sol,
  });

  const registerLangSelectAria =
    ui.nav_lang_select || ui.grid_labels?.language || enMaster.ui.nav_lang_select || 'Language';

  const registerA11yProps = {
    registerFontScaleMode: true,
    registerFontScale,
    onRegisterFontScaleChange: (v) => setRegisterFontScale(clampRegisterFontScale(v)),
    accessibilityTriggerIconOnly: true,
  };

  const proofCheckout = isProofOffer(appliedOffer);

  return (
    <>
    <div
      className="registration-root"
      style={{
      background: getDynamicBackground(), transition: 'background 2s ease-in-out', 
      minHeight: '100vh'
    }} dir={currentLang.dir}>

      {view === 'SUCCESS' && (
        <div className="reg-success-shell">
      
      <div style={{ textAlign: 'center', maxWidth: '800px', width: '100%' }} className="fade-in">
        <img src="/images/01%20IFIP%20Logo.png" alt="IFIP Logo" style={{ height: '80px', marginBottom: '40px' }} />
        
        <div className="reg-success-card" style={{ background: 'var(--reg-success-card-bg)', padding: '50px', borderRadius: '32px', border: '1px solid var(--reg-success-card-border)', boxShadow: '0 40px 100px rgba(0,0,0,0.4)' }}>
          <h1 style={{ color: 'var(--a11y-page-text)', fontSize: '2.2rem', fontWeight: '300', marginBottom: '20px' }}>{flow.success_payment_confirmed}</h1>
          
          {/* Magic Link Frame */}
          <div style={{ marginTop: '40px', textAlign: 'left' }}>
            <label style={{ display: 'block', color: 'var(--reg-label-muted)', fontSize: '0.85rem', marginBottom: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {flow.success_copy_link_label}
            </label>
            <div className="reg-success-link-row" style={{ 
              background: 'var(--reg-magic-link-box-bg)', padding: '8px', borderRadius: '16px', border: '1px solid var(--a11y-chrome)',
            }}>
              <code style={{ flex: 1, padding: '0 15px', color: 'var(--reg-magic-link-code)', fontSize: '0.95rem', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {generatedMagicLink}
              </code>
              <button 
                onClick={handleCopyLink}
                style={{ 
                  background: copySuccess ? '#138a8a' : 'rgba(19, 138, 138, 0.2)', 
                  color: 'var(--a11y-btn-text)', border: 'none', padding: '12px 25px', borderRadius: '12px', 
                  cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', transition: '0.3s' 
                }}
              >
                {copySuccess ? flow.success_copied : flow.success_copy_btn}
              </button>
            </div>
          </div>

          <div style={{ marginTop: '40px', color: 'var(--a11y-page-text)', opacity: 0.88, lineHeight: '1.7', textAlign: 'left' }}>
            <p style={{ marginBottom: '15px' }}>{flow.success_portal_guide}</p>
            <p style={{ marginBottom: '25px', fontWeight: 'bold', color: '#138a8a' }}>{flow.success_email_note}</p>
            
            <div style={{ background: 'rgba(209, 29, 122, 0.1)', borderLeft: '4px solid #d11d7a', padding: '20px', borderRadius: '4px', marginBottom: '40px' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--a11y-page-text)' }}>{flow.success_security_important}</p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '20px', opacity: 0.6 }}>{flow.success_proceed_prompt}</p>
              <button 
                onClick={() => window.location.href = generatedMagicLink}
                style={{ 
                  padding: '18px 50px', borderRadius: '100px', background: 'var(--a11y-btn-bg)', color: 'var(--a11y-btn-text)', 
                  border: 'none', fontWeight: 'bold', cursor: 'pointer', fontSize: '1.1rem', transition: '0.3s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                {flow.btn_go_to_portal}
              </button>
              <p style={{ marginTop: '30px', fontSize: '0.85rem', opacity: 0.4 }}>{flow.success_safe_to_close}</p>
            </div>
          </div>
        </div>
      </div>
      </div>
    )}
    

      {/* Global floating language + accessibility (non-form, non-splash) */}
      {view !== 'FORM' && view !== 'SPLASH' && (
        <div className="register-chrome-floating">
          <select
            className="register-chrome-floating-select"
            value={langKey}
            onChange={(e) => setLangKey(e.target.value)}
            style={{
              background: 'var(--reg-glass-bg-soft)',
              color: 'var(--a11y-page-text)',
              border: '1px solid var(--reg-glass-border)',
              cursor: 'pointer',
              outline: 'none',
            }}
            aria-label={registerLangSelectAria}
          >
            {Object.entries(languageManifest)
              .sort((a, b) => (a[1].nativeName || a[1].label || a[0]).localeCompare(b[1].nativeName || b[1].label || b[0]))
              .map(([key, lang]) => (
                <option key={key} value={key} style={{ color: 'black' }}>
                  {lang.nativeName}
                </option>
              ))}
          </select>
          <AccessibilityControls
            panelOpen={a11yPanelOpen}
            setPanelOpen={setA11yPanelOpen}
            scheme={a11yScheme}
            setScheme={setA11yScheme}
            labels={pickRegisterA11yLabels()}
            rootClassName="ifip-a11y-control-root ifip-a11y-control-root--header"
            panelId="a11y-panel-register-float"
            {...registerA11yProps}
          />
          <SupportHelpButton
            label={flow.help_btn || REG_FLOW_STRINGS.help_btn}
            onClick={() => setShowSupportModal(true)}
          />
        </div>
      )}

      {/* 1. SPLASH VIEW */}
      {view === 'SPLASH' && (
        <div className="splash-wrapper">
          <div className="word-container">
            <div className="letter">I</div>
            <div className="letter">F</div>
            <div className="i-column">
              <div className="zing-flare"></div>
              <img src="/images/01%20IFIP%20Logo.png" className="globe-dot" alt="IFIP Globe" />
              <span className="squash-i">I</span>
            </div>
            <div className="letter">P</div>
          </div>
          <div className="entrance-controls" style={{ opacity: 1, animation: 'fadeIn 1s forwards 2s' }}>
            <h2 style={{ fontWeight: '300', letterSpacing: '2px', marginBottom: '30px', color: 'var(--a11y-page-text)', opacity: 0.95 }}>
              {flow.label_accreditation} <span style={{ fontWeight: '800', color: 'var(--a11y-page-text)' }}>{flow.label_registration}</span>
            </h2>
            <button className="enter-button" onClick={() => setView('INTRO')}>
              {flow.btn_enter_registration}
            </button>
            <div className="ifip-splash-toolbar-row ifip-splash-toolbar-row--register-chrome">
              <AccessibilityControls
                panelOpen={a11yPanelOpen}
                setPanelOpen={setA11yPanelOpen}
                scheme={a11yScheme}
                setScheme={setA11yScheme}
                labels={pickRegisterA11yLabels()}
                rootClassName="ifip-a11y-control-root ifip-a11y-control-root--splash-inline-toolbar"
                panelId="a11y-panel-register-splash"
                {...registerA11yProps}
                splashLangSelect={
                  <select
                    className="ifip-splash-lang-select"
                    value={langKey}
                    onChange={(e) => {
                      const next = e.target.value;
                      setLangKey(next);
                      localStorage.setItem('ifip_lang', next);
                    }}
                    aria-label={registerLangSelectAria}
                  >
                    {Object.entries(languageManifest)
                      .sort((a, b) =>
                        (a[1].nativeName || a[1].label || a[0]).localeCompare(
                          b[1].nativeName || b[1].label || b[0]
                        )
                      )
                      .map(([key, lang]) => (
                        <option key={key} value={key} style={{ color: 'black' }}>
                          {lang.nativeName}
                        </option>
                      ))}
                  </select>
                }
              />
              <SupportHelpButton
                label={flow.help_btn || REG_FLOW_STRINGS.help_btn}
                onClick={() => setShowSupportModal(true)}
              />
            </div>
          </div>
        </div>
      )}

      {/* 2. INTRO VIEW (USER GUIDE) */}
      {view === 'INTRO' && (
        <main className="reg-intro-main">
          <div className="fade-in reg-intro-card" style={{ maxWidth: '800px', textAlign: 'center', background: 'var(--reg-glass-bg)', padding: '60px', borderRadius: '40px', border: '1px solid var(--reg-glass-border)', backdropFilter: 'blur(30px)', boxSizing: 'border-box', boxShadow: '0 30px 60px rgba(0,0,0,0.4)' }}>
            
            {/* Logo Header */}
            <img src="/images/01%20IFIP%20Logo.png" alt="IFIP Logo" style={{ height: '70px', marginBottom: '35px' }} />
            
            <div style={{ color: 'var(--a11y-page-text)', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '300', marginBottom: '25px', borderBottom: '1px solid var(--reg-glass-border-subtle)', paddingBottom: '20px', lineHeight: '1.4' }}>
                {flow.intro_guide_p1}
              </h2>
              
              <div style={{ marginBottom: '35px', fontSize: '1.05rem', lineHeight: '1.7' }}>
                <p style={{ marginBottom: '15px' }}>{flow.intro_guide_p2}</p>
                <p>{flow.intro_guide_p3}</p>
              </div>

              {/* High-Contrast Fees Frame */}
              <div style={{ background: 'var(--reg-fee-callout-bg)', border: '2px solid var(--a11y-chrome)', padding: '35px', borderRadius: '20px', marginBottom: '40px', boxShadow: '0 15px 35px rgba(0,0,0,0.3)' }}>
                <h3 style={{ color: '#138a8a', marginTop: 0, textTransform: 'uppercase', letterSpacing: '3px', fontSize: '0.9rem', fontWeight: '900', marginBottom: '15px' }}>
                  {flow.intro_fee_title}
                </h3>
                <p style={{ margin: 0, fontSize: '1.1rem', color: 'var(--a11y-page-text)', lineHeight: '1.6', fontWeight: '400' }}>
                  {flow.intro_guide_fee_box}
                </p>
              </div>

              <p style={{ fontSize: '0.95rem', opacity: 0.8, marginBottom: '40px' }}>
                {flow.intro_guide_p5} {' '}
                <a href="https://www.ifip.group/" target="_blank" rel="noreferrer" style={{ color: '#138a8a', textDecoration: 'underline', fontWeight: 'bold' }}>
                  www.ifip.group
                </a>
              </p>
            </div>

            <div className="reg-intro-actions">
              <button className="enter-button" style={{ padding: '20px 50px', fontSize: '1.1rem' }} onClick={() => setView('FORM')}>{flow.btn_start_new_reg}</button>
              <button className="back-button" style={{ padding: '20px 40px' }} onClick={() => setView('RECOVERY')}>{flow.btn_go_to_recovery}</button>
            </div>
          </div>
        </main>
      )}
      {/* 3. RECOVERY VIEW (BLIND CONFIRMATION) */}
      {view === 'RECOVERY' && (
        <main className="reg-recovery-main">
          <div className="fade-in reg-recovery-card" style={{ maxWidth: '600px', width: '100%', background: 'var(--reg-glass-bg-soft)', padding: '50px', borderRadius: '30px', border: '1px solid var(--reg-glass-border-subtle)' }}>
            <h2 style={{ color: 'var(--a11y-page-text)', marginBottom: '20px' }}>{flow.recovery_title}</h2>
            {recoverySent ? (
              <div style={{ background: 'rgba(19, 138, 138, 0.1)', border: '1px solid #138a8a', padding: '20px', borderRadius: '12px', color: 'var(--a11y-page-text)', lineHeight: '1.6' }}>
                {flow.recovery_blind_conf}
                <button onClick={() => setView('INTRO')} style={{ display: 'block', marginTop: '20px', color: '#138a8a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>← {flow.btn_back_to_intro}</button>
              </div>
            ) : (
              <form onSubmit={handleCheckAccess}>
                <p style={{ color: 'var(--a11y-page-text)', opacity: 0.72, marginBottom: '25px' }}>{flow.recovery_prompt}</p>
                <input type="email" required value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} placeholder={flow.placeholder_recovery_email} style={{ width: '100%', padding: '15px', borderRadius: '8px', background: 'var(--a11y-input-bg)', border: '1px solid var(--a11y-surface-border)', color: 'var(--a11y-input-text)', marginBottom: '20px' }} />
                <button type="submit" className="enter-button" style={{ width: '100%' }}>{flow.btn_send_recovery}</button>
                <button type="button" onClick={() => setView('INTRO')} style={{ width: '100%', background: 'none', border: 'none', color: 'var(--a11y-page-text)', opacity: 0.5, marginTop: '15px', cursor: 'pointer' }}>{flow.btn_back_to_intro}</button>
              </form>
            )}
          </div>
        </main>
      )}

      {/* 4. CHECKOUT VIEW */}
      {view === 'CHECKOUT' && (
        <main className="reg-checkout-main">
          
          {/* Prominent Branding with Minimal Top Padding */}
          <div style={{ textAlign: 'center', marginBottom: '25px', marginTop: '20px' }} className="fade-in">
            <img src="/images/01%20IFIP%20Logo.png" alt="IFIP Logo" style={{ height: '90px', marginBottom: '15px', filter: 'drop-shadow(0 5px 15px rgba(0,0,0,0.2))' }} />
            <h2 style={{ color: 'var(--a11y-page-text)', fontSize: '2.2rem', fontWeight: '300', marginBottom: '5px' }}>{flow.checkout_title}</h2>
            <p style={{ color: 'var(--a11y-page-text)', opacity: 0.55, fontSize: '1rem', maxWidth: '550px', margin: '0 auto' }}>
              {proofCheckout
                ? (flow.checkout_partner_desc || REG_FLOW_STRINGS.checkout_partner_desc)
                : flow.checkout_desc}
            </p>
          </div>

          <div className="reg-checkout-shell">
            {/* Back Button to return to the questionnaire */}
            <button 
              className="reg-checkout-back"
              onClick={() => setView('FORM')}
              style={{ 
                position: 'absolute', top: '-50px', left: '0', background: 'none', border: 'none', 
                color: 'var(--a11y-page-text)', opacity: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', 
                gap: '8px', fontSize: '0.9rem', fontWeight: 'bold', padding: '10px 0', transition: '0.3s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.5}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
              {t.reg_ui.btn_back}
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '25px' }}>
              {proofCheckout ? (
                <section
                  style={{
                    gridColumn: '1 / -1',
                    padding: '50px 40px',
                    borderRadius: '32px',
                    textAlign: 'center',
                    background: 'rgba(19, 138, 138, 0.1)',
                    border: '2px solid #138a8a',
                  }}
                >
                  <h3 style={{ color: 'var(--a11y-page-text)', fontSize: '1.6rem', marginBottom: '16px', fontWeight: 300 }}>
                    {flow.checkout_partner_title || REG_FLOW_STRINGS.checkout_partner_title}
                  </h3>
                  <p style={{ color: 'var(--a11y-page-text)', opacity: 0.85, fontSize: '1rem', lineHeight: 1.6, maxWidth: '560px', margin: '0 auto' }}>
                    {flow.checkout_partner_desc || REG_FLOW_STRINGS.checkout_partner_desc}
                  </p>
                </section>
              ) : paymentChoice === 'bespoke' ? (
                /* Bespoke Market View - Replaces standard payment cards when service is restricted */
                <div style={{ 
                  gridColumn: '1 / -1', padding: '60px 40px', borderRadius: '32px', textAlign: 'center',
                  background: 'rgba(209, 29, 122, 0.05)', border: '2px dashed #d11d7a', boxSizing: 'border-box'
                }}>
                  <div style={{ fontSize: '3.5rem', marginBottom: '25px' }}>🌍</div>
                  <h3 style={{ color: 'var(--a11y-page-text)', fontSize: '1.8rem', marginBottom: '20px', fontWeight: '300' }}>Bespoke Accreditation Path</h3>
                  <p style={{ color: 'var(--a11y-page-text)', opacity: 0.92, fontSize: '1.1rem', lineHeight: '1.7', maxWidth: '650px', margin: '0 auto 30px auto' }}>
                    {flow.error_bespoke_service}
                  </p>
                  <a 
                    href={`mailto:${flow.error_bespoke_email}?subject=Bespoke Accreditation Inquiry: ${formData.answers['officialName']}`}
                    style={{ 
                      display: 'inline-block', padding: '18px 45px', background: '#d11d7a', color: 'var(--a11y-btn-text)', 
                      borderRadius: '100px', textDecoration: 'none', fontWeight: 'bold', fontSize: '1.1rem',
                      boxShadow: '0 10px 20px rgba(209, 29, 122, 0.3)', transition: '0.3s'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    {flow.btn_contact_ifip}
                  </a>
                  <div style={{ marginTop: '30px', fontSize: '0.85rem', color: 'var(--a11y-page-text)', opacity: 0.45 }}>
                    Official Registrar: {flow.error_bespoke_email}
                  </div>
                </div>
              ) : (
                <>
                  {/* Card Path */}
                  <div 
                    onClick={() => setPaymentChoice('card')} 
                style={{ 
                  position: 'relative', padding: '35px 30px', borderRadius: '24px', cursor: 'pointer', transition: '0.3s ease',
                  background: paymentChoice === 'card' ? 'var(--reg-pay-sel-card)' : 'var(--reg-pay-unsel)',
                  borderWidth: '3px', borderStyle: 'solid', borderColor: paymentChoice === 'card' ? '#138a8a' : 'var(--reg-glass-border-faint)',
                  boxShadow: paymentChoice === 'card' ? 'inset 0 4px 12px rgba(0,0,0,0.5)' : 'none'
                }}
              >
                {paymentChoice === 'card' && (
                  <div style={{ 
                    position: 'absolute', top: '15px', right: '20px', background: '#138a8a', 
                    width: '28px', height: '28px', borderRadius: '50%', display: 'flex', 
                    alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' 
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                )}
                <div style={{ width: '45px', height: '45px', borderRadius: '10px', background: '#138a8a', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                </div>
                <h3 style={{ color: 'var(--a11y-page-text)', fontSize: '1.5rem', margin: '0 0 8px 0' }}>{flow.card_title}</h3>
                <p style={{ color: 'var(--a11y-page-text)', opacity: 0.65, fontSize: '0.9rem', lineHeight: '1.5', margin: '0 0 20px 0' }}>{flow.card_desc}</p>
                <ul style={{ padding: 0, listStyle: 'none', color: '#138a8a', fontSize: '0.85rem', fontWeight: 'bold', borderTop: '1px solid var(--reg-glass-border-faint)', paddingTop: '15px' }}>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>✓ {flow.card_feature_1}</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>✓ {flow.card_feature_2}</li>
                </ul>
              </div>

              {/* Invoice Path */}
              <div 
                onClick={() => setPaymentChoice('invoice')} 
                style={{ 
                  position: 'relative', padding: '35px 30px', borderRadius: '24px', cursor: 'pointer', transition: '0.3s ease',
                  background: paymentChoice === 'invoice' ? 'var(--reg-pay-sel-invoice)' : 'var(--reg-pay-unsel)',
                  borderWidth: '3px', borderStyle: 'solid', borderColor: paymentChoice === 'invoice' ? '#94a3b8' : 'var(--reg-glass-border-faint)',
                  boxShadow: paymentChoice === 'invoice' ? 'inset 0 4px 12px rgba(0,0,0,0.5)' : 'none'
                }}
              >
                {paymentChoice === 'invoice' && (
                  <div style={{ 
                    position: 'absolute', top: '15px', right: '20px', background: '#94a3b8', 
                    width: '28px', height: '28px', borderRadius: '50%', display: 'flex', 
                    alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' 
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                )}
                <div style={{ width: '45px', height: '45px', borderRadius: '10px', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <h3 style={{ color: 'var(--a11y-page-text)', fontSize: '1.5rem', margin: '0 0 8px 0' }}>{flow.invoice_title}</h3>
                <p style={{ color: 'var(--a11y-page-text)', opacity: 0.65, fontSize: '0.9rem', lineHeight: '1.5', margin: '0 0 20px 0' }}>{flow.invoice_desc}</p>
                <ul style={{ padding: 0, listStyle: 'none', color: '#94a3b8', fontSize: '0.85rem', fontWeight: 'bold', borderTop: '1px solid var(--reg-glass-border-faint)', paddingTop: '15px' }}>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>✓ {flow.invoice_feature_1}</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>✓ {flow.invoice_feature_2}</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', background: 'var(--reg-checkout-fee-strip)', padding: '20px 30px', borderRadius: '24px', border: '1px solid var(--reg-glass-border-faint)', marginBottom: '30px' }}>
              {appliedOffer && (() => {
                const codeType = resolveOfferCodeType(appliedOffer);
                const isProof = codeType === OFFER_CODE_TYPES.PROOF_OF_PURCHASE;
                const appliedTemplate = isProof
                  ? flow.offer_applied_proof || REG_FLOW_STRINGS.offer_applied_proof
                  : flow.offer_applied_discount || REG_FLOW_STRINGS.offer_applied_discount;
                const note = isProof
                  ? flow.offer_partner_note || REG_FLOW_STRINGS.offer_partner_note
                  : flow.offer_discount_note || REG_FLOW_STRINGS.offer_discount_note;
                return (
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '14px 18px',
                      borderRadius: '14px',
                      textAlign: 'left',
                      border: isProof ? '1px solid #d97706' : '1px solid #138a8a',
                      background: isProof ? 'rgba(217,119,6,0.12)' : 'rgba(19,138,138,0.12)',
                    }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span
                        style={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          padding: '4px 10px',
                          borderRadius: '100px',
                          border: isProof ? '1px solid #d97706' : '1px solid #138a8a',
                          color: isProof ? '#fbbf24' : '#5eead4',
                          background: isProof ? 'rgba(217,119,6,0.2)' : 'rgba(19,138,138,0.2)',
                        }}
                      >
                        {offerCodeTypeLabel(codeType)}
                      </span>
                      <strong style={{ color: 'var(--a11y-page-text)', fontSize: '0.95rem' }}>
                        {appliedTemplate.replace(/\{\{code\}\}/g, appliedOffer.code)}
                      </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.55, opacity: 0.88, color: 'var(--a11y-page-text)' }}>{note}</p>
                  </div>
                );
              })()}
              {!proofCheckout && (
              <p style={{ color: 'var(--a11y-page-text)', opacity: 0.55, fontSize: '0.85rem', margin: paymentChoice === 'invoice' ? '0 0 15px 0' : '0' }}>
                {flow.fee_preselected_note.replace('{{country}}', formData.answers['officialAddressCountry'] || 'your country')}
              </p>
              )}
              {checkoutFeeDisplay && paymentChoice !== 'bespoke' && (
                <p style={{ color: 'var(--a11y-page-text)', fontSize: '1.35rem', fontWeight: 700, margin: '12px 0 0 0', letterSpacing: '0.02em' }}>
                  <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.55, fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase' }}>
                    {flow.checkout_fee_amount_label || REG_FLOW_STRINGS.checkout_fee_amount_label}
                  </span>
                  {checkoutFeeDisplay}
                </p>
              )}
              
              {paymentChoice === 'invoice' && (
                <div className="fade-in" style={{ maxWidth: '400px', margin: '0 auto' }}>
                  <input type="email" value={financeEmail} onChange={(e) => setFinanceEmail(e.target.value)} placeholder={flow.finance_contact_label} style={{ width: '100%', padding: '12px 25px', borderRadius: '100px', background: 'var(--a11y-input-bg)', border: '1px solid var(--a11y-surface-border)', color: 'var(--a11y-input-text)', outline: 'none', fontSize: '0.9rem' }} />
                </div>
              )}
            </div>

            {(proofCheckout || paymentChoice !== 'bespoke') && (
              <button 
                onClick={handleCheckoutFinalize} 
                disabled={isSubmitting || (proofCheckout && !appliedOffer)} 
                className="enter-button" 
                style={{ 
                  width: '100%', padding: '22px', fontSize: '1.25rem', fontWeight: 'bold', borderRadius: '100px',
                  background: proofCheckout || paymentChoice === 'card' ? '#138a8a' : '#334155', border: 'none', transition: '0.3s',
                  marginBottom: '20px' 
                }}
              >
                {isSubmitting
                  ? (proofCheckout
                      ? (flow.status_completing_registration || REG_FLOW_STRINGS.status_completing_registration)
                      : flow.status_connecting_billing)
                  : proofCheckout
                    ? (flow.btn_complete_registration || REG_FLOW_STRINGS.btn_complete_registration)
                    : flow.btn_confirm_path}
              </button>
            )}
          </div>
        </main>
      )}

      {/* 5. FORM VIEW */}
      {view === 'FORM' && (
        <>
          <div className="reg-form-header-sticky">
            <header className="reg-form-header">
              {isTranslating && (
                <div style={{ position: 'absolute', top: '-25px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                  <span className="translating-dots" style={{ fontSize: '0.6rem', color: '#138a8a', textTransform: 'uppercase', letterSpacing: '1px' }}>{flow.status_localizing}</span>
                </div>
              )}
              <div className="reg-form-header-brand">
                <img src="/images/01%20IFIP%20Logo.png" alt="IFIP" style={{ height: '32px', flexShrink: 0 }} />
                <div className="reg-form-header-progress-wrap">
                  <h1 style={{ fontSize: '0.65rem', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--a11y-page-text)', marginBottom: '6px', textAlign: 'center', opacity: 0.72 }}>
                    {t.reg_ui.title}
                  </h1>
                  <div style={{ width: '100%', display: 'flex', gap: '3px', height: '3px' }}>
                    {Array.from({ length: totalSteps }).map((_, idx) => (
                      <div key={idx} style={{ flex: 1, borderRadius: '2px', background: idx <= currentStep ? '#138a8a' : 'var(--reg-form-pill-track-off)', transition: '0.6s' }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="reg-form-header-tools">
                <select
                  className="register-chrome-header-select"
                  value={langKey}
                  onChange={(e) => {
                    setLangKey(e.target.value);
                    setCurrentStep(0);
                  }}
                  aria-label={registerLangSelectAria}
                  style={{
                    background: 'transparent',
                    color: 'var(--a11y-page-text)',
                    border: '1px solid var(--reg-glass-border-subtle)',
                  }}
                >
                  {Object.entries(languageManifest)
                    .sort((a, b) => (a[1].nativeName || a[1].label || a[0]).localeCompare(b[1].nativeName || b[1].label || b[0]))
                    .map(([key, lang]) => (
                      <option key={key} value={key} style={{ color: '#000' }}>
                        {lang.nativeName}
                      </option>
                    ))}
                </select>
                <AccessibilityControls
                  panelOpen={a11yPanelOpen}
                  setPanelOpen={setA11yPanelOpen}
                  scheme={a11yScheme}
                  setScheme={setA11yScheme}
                  labels={pickRegisterA11yLabels()}
                  rootClassName="ifip-a11y-control-root ifip-a11y-control-root--header"
                  panelId="a11y-panel-register-form"
                  {...registerA11yProps}
                />
                <SupportHelpButton
                  label={flow.help_btn || REG_FLOW_STRINGS.help_btn}
                  onClick={() => setShowSupportModal(true)}
                />
              </div>
            </header>
          </div>

          <main className="reg-form-main">
            {duplicateFound && (
              <div className="fade-in" style={{ maxWidth: '850px', margin: '0 auto 20px auto', background: 'rgba(209, 29, 126, 0.1)', border: '1px solid #d11d7e', padding: '20px', borderRadius: '12px', color: 'var(--a11y-page-text)', textAlign: 'center' }}>
                {flow.error_duplicate_name}
              </div>
            )}
            <form onSubmit={handleSubmit} className="reg-form-card" style={{ 
              background: 'var(--reg-glass-bg-softer)', 
              padding: '50px', borderRadius: '30px', border: '1px solid var(--reg-success-card-border)', 
              boxShadow: '0 40px 100px rgba(0,0,0,0.3)', color: 'var(--a11y-page-text)'
            }}>
              
              {resumptionStatus ? (
                <div className="fade-in" style={{ textAlign: 'center', padding: '20px 0' }}>
                  <h2 style={{ color: 'var(--a11y-page-text)', marginBottom: '20px', fontWeight: '300', fontSize: '1.8rem' }}>{flow.resumption_found_title}</h2>
                  {resumptionStatus === 'sent' ? (
                    <div style={{ background: 'rgba(19, 138, 138, 0.1)', border: '1px solid #138a8a', padding: '30px', borderRadius: '16px' }}>
                      <p style={{ color: 'var(--a11y-page-text)', margin: 0, lineHeight: '1.6' }}>{flow.resumption_sent_msg}</p>
                    </div>
                  ) : (
                    <div style={{ background: 'var(--reg-glass-bg-faint)', padding: '30px', borderRadius: '16px', border: '1px solid var(--reg-glass-border-subtle)' }}>
                      <p style={{ color: 'var(--a11y-page-text)', opacity: 0.72, marginBottom: '30px', lineHeight: '1.7' }}>{flow.resumption_found_body}</p>
                      <button type="button" onClick={handleRequestResumption} disabled={isSubmitting} className="enter-button" style={{ width: '100%' }}>
                        {isSubmitting ? "..." : flow.btn_send_resumption}
                      </button>
                    </div>
                  )}
                  <button type="button" onClick={() => setResumptionStatus(null)} style={{ marginTop: '30px', background: 'none', border: 'none', color: 'var(--a11y-page-text)', opacity: 0.5, cursor: 'pointer', fontWeight: 'bold' }}>
                    ← {t.reg_ui.btn_back}
                  </button>
                </div>
              ) : currentStep === 0 ? (
                <div className="fade-in" style={{ textAlign: 'center' }}>
                  {step0Screen === 'paused' ? (
                    <div style={{ maxWidth: '620px', margin: '0 auto', padding: '24px' }}>
                      <h2 style={{ color: '#d11d7a', fontWeight: 300, fontSize: '1.8rem' }}>
                        {flow.registrations_paused_title || REG_FLOW_STRINGS.registrations_paused_title}
                      </h2>
                      <p style={{ opacity: 0.8, lineHeight: 1.7, color: 'var(--a11y-page-text)' }}>
                        {flow.registrations_paused_body || REG_FLOW_STRINGS.registrations_paused_body}
                      </p>
                      <button type="button" onClick={() => setStep0Screen('select')} style={{ marginTop: '24px', background: 'none', border: 'none', color: '#138a8a', cursor: 'pointer', fontWeight: 'bold' }}>
                        ← {t.reg_ui.btn_back}
                      </button>
                    </div>
                  ) : step0Screen === 'market_gate' || step0Screen === 'partner_gate' ? (
                    (() => {
                      const country = formData.answers?.officialAddressCountry || '';
                      const level = formData.accreditationLevel;
                      const levelLabel = levelDisplayLabel(level);
                      const feeData = pricingConfig
                        ? adminServices.getCalculatedFee(country, level, pricingConfig)
                        : { partnerName: '' };
                      const alternatives = pricingConfig
                        ? getAlternativeLevels(country, level, pricingConfig)
                        : [];
                      const altLabels = alternatives.map(levelDisplayLabel).join(' or ');
                      const gateTitle =
                        step0Screen === 'partner_gate'
                          ? flow.partner_gate_title || REG_FLOW_STRINGS.partner_gate_title
                          : flow.market_gate_title || REG_FLOW_STRINGS.market_gate_title;
                      const gateBodyTemplate =
                        step0Screen === 'partner_gate'
                          ? (feeData.partnerName
                            ? flow.partner_gate_body || REG_FLOW_STRINGS.partner_gate_body
                            : flow.partner_gate_body_no_name || REG_FLOW_STRINGS.partner_gate_body_no_name)
                          : flow.market_gate_body || REG_FLOW_STRINGS.market_gate_body;
                      const gateBody = gateBodyTemplate
                        .replace(/\{\{country\}\}/g, country)
                        .replace(/\{\{levelLabel\}\}/g, levelLabel)
                        .replace(/\{\{partnerName\}\}/g, feeData.partnerName || 'our local partner')
                        .replace(/\{\{alternatives\}\}/g, altLabels || levelDisplayLabel('foundation'));
                      return (
                        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'left' }}>
                          <h2 style={{ textAlign: 'center', color: '#d11d7a', fontWeight: 300, fontSize: '1.7rem', marginBottom: '16px' }}>{gateTitle}</h2>
                          <p style={{ opacity: 0.88, lineHeight: 1.7, color: 'var(--a11y-page-text)', marginBottom: '28px' }}>{gateBody}</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '28px', justifyContent: 'center' }}>
                            <a
                              href={`mailto:${flow.error_bespoke_email || REG_FLOW_STRINGS.error_bespoke_email}?subject=IFIP accreditation inquiry: ${encodeURIComponent(levelLabel)} — ${encodeURIComponent(country)}`}
                              style={{ display: 'inline-block', padding: '12px 22px', borderRadius: '100px', background: '#d11d7a', color: 'var(--a11y-btn-text)', textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem' }}
                            >
                              {flow.btn_contact_ifip_discuss || REG_FLOW_STRINGS.btn_contact_ifip_discuss}
                            </a>
                            {alternatives.map((altLevel) => (
                              <button
                                key={altLevel}
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({ ...prev, accreditationLevel: altLevel }));
                                  setAppliedOffer(null);
                                  setOfferCodeDraft('');
                                  advanceFromStep0Gate();
                                }}
                                style={{ padding: '12px 22px', borderRadius: '100px', border: '1px solid #138a8a', background: 'rgba(19,138,138,0.12)', color: '#138a8a', fontWeight: 700, cursor: 'pointer' }}
                              >
                                {(flow.btn_continue_with_level || REG_FLOW_STRINGS.btn_continue_with_level).replace('{{levelLabel}}', levelDisplayLabel(altLevel))}
                              </button>
                            ))}
                          </div>
                          <div style={{ padding: '22px', borderRadius: '16px', border: '2px solid #138a8a', background: 'rgba(19, 138, 138, 0.1)' }}>
                            <h3 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#138a8a' }}>
                              {step0Screen === 'partner_gate'
                                ? flow.offer_panel_title_partner || REG_FLOW_STRINGS.offer_panel_title_partner
                                : flow.offer_panel_title_market || REG_FLOW_STRINGS.offer_panel_title_market}
                            </h3>
                            <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', lineHeight: 1.55, opacity: 0.88, color: 'var(--a11y-page-text)' }}>
                              {step0Screen === 'partner_gate'
                                ? flow.offer_panel_hint_partner || REG_FLOW_STRINGS.offer_panel_hint_partner
                                : flow.offer_panel_desc_market || REG_FLOW_STRINGS.offer_panel_desc_market}
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                              <input
                                type="text"
                                value={offerCodeDraft}
                                onChange={(e) => setOfferCodeDraft(e.target.value.toUpperCase())}
                                placeholder={flow.offer_code_placeholder || REG_FLOW_STRINGS.offer_code_placeholder}
                                style={{ flex: '1 1 200px', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--a11y-surface-border)', background: 'var(--a11y-input-bg)', color: 'var(--a11y-input-text)' }}
                              />
                              <button type="button" onClick={handleApplyOfferAndMaybeContinue} disabled={offerValidating || !offerCodeDraft.trim()} className="enter-button" style={{ padding: '12px 20px' }}>
                                {offerValidating ? '…' : (flow.btn_enter_code_continue || REG_FLOW_STRINGS.btn_enter_code_continue)}
                              </button>
                            </div>
                            {renderAppliedOfferFeedback()}
                            {offerError && <p style={{ color: '#f87171', fontSize: '0.85rem', marginTop: '10px' }}>{offerError}</p>}
                          </div>
                          <button type="button" onClick={() => setStep0Screen('select')} style={{ marginTop: '28px', background: 'none', border: 'none', color: 'var(--a11y-page-text)', opacity: 0.6, cursor: 'pointer', fontWeight: 'bold', width: '100%', textAlign: 'center' }}>
                            ← {t.reg_ui.btn_back}
                          </button>
                        </div>
                      );
                    })()
                  ) : (
                    <>
                  <h1 style={{ fontWeight: '300', fontSize: '2.2rem', color: 'var(--a11y-page-text)', marginBottom: '10px' }}>{t.title}</h1>
                  <p style={{ opacity: 0.65, fontSize: '1rem', maxWidth: '650px', margin: '0 auto 28px auto', lineHeight: '1.6', color: 'var(--a11y-page-text)' }}>{t.intro}</p>

                  <div style={{ maxWidth: '620px', margin: '0 auto 32px auto', textAlign: 'left' }}>
                    <h2 style={{ marginBottom: '12px', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '3px', color: '#138a8a' }}>
                      {flow.step0_country_title || REG_FLOW_STRINGS.step0_country_title}
                    </h2>
                    <select
                      value={formData.answers.officialAddressCountry || ''}
                      onChange={(e) => {
                        if (appliedOffer && e.target.value !== formData.answers.officialAddressCountry) {
                          setAppliedOffer(null);
                          persistAppliedOffer(null);
                          setOfferCodeDraft('');
                          setOfferError('');
                        }
                        handleInputChange('officialAddressCountry', e.target.value);
                      }}
                      required
                      style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', background: 'var(--a11y-input-bg)', color: 'var(--a11y-input-text)', border: '1px solid var(--a11y-surface-border)', fontSize: '1rem' }}
                    >
                      <option value="" style={{ color: 'black' }}>{flow.placeholder_country}</option>
                      {countryEndonyms.map((country) => (
                        <option key={country} value={country} style={{ color: 'black' }}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <h2 style={{ marginBottom: '30px', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '3px', color: '#138a8a' }}>
                    {flow.step0_level_title || REG_FLOW_STRINGS.step0_level_title}
                  </h2>

                  <div className="reg-step0-level-grid">
                    {['foundation', 'certified', 'accredited', 'notSure'].map((key) => {
                      const stage = t.stages[key];
                      if (!stage) return null;
                      return (
                        <label key={key} style={{
                          padding: '25px', border: '1px solid var(--reg-glass-border-subtle)', borderRadius: '16px', cursor: 'pointer', transition: '0.3s',
                          background: formData.accreditationLevel === key ? 'rgba(19, 138, 138, 0.15)' : 'var(--reg-inset-bg)'
                        }}>
                          <input type="radio" name="level" style={{ display: 'none' }} checked={formData.accreditationLevel === key} onChange={() => {
                            if (formData.accreditationLevel !== key && appliedOffer) {
                              setAppliedOffer(null);
                              persistAppliedOffer(null);
                              setOfferCodeDraft('');
                              setOfferError('');
                            }
                            setFormData({ ...formData, accreditationLevel: key });
                          }} />
                          <strong style={{ display: 'block', fontSize: '1.1rem', color: formData.accreditationLevel === key ? '#138a8a' : 'var(--a11y-page-text)', marginBottom: '8px' }}>{stage.title}</strong>
                          <p style={{ fontSize: '0.85rem', opacity: 0.58, lineHeight: '1.4', margin: 0, color: 'var(--a11y-page-text)' }}>{stage.desc}</p>
                        </label>
                      );
                    })}
                  </div>

                  <p style={{ marginTop: '28px', fontSize: '0.92rem', opacity: 0.72, color: 'var(--a11y-page-text)' }}>
                    {flow.wizard_link_prefix || REG_FLOW_STRINGS.wizard_link_prefix}{' '}
                    <Link to="/accreditation-wizard" style={{ color: '#138a8a', fontWeight: 600, textDecoration: 'underline' }}>
                      {flow.wizard_link_cta || REG_FLOW_STRINGS.wizard_link_cta}
                    </Link>
                  </p>

                  <div
                    style={{
                      maxWidth: '620px',
                      margin: '32px auto 0 auto',
                      textAlign: 'left',
                      padding: '20px 22px',
                      borderRadius: '16px',
                      border: '1px solid #138a8a',
                      background: 'rgba(19, 138, 138, 0.08)',
                    }}
                  >
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#138a8a' }}>
                      {flow.offer_panel_title || REG_FLOW_STRINGS.offer_panel_title}
                    </h3>
                    <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', lineHeight: 1.55, opacity: 0.88, color: 'var(--a11y-page-text)' }}>
                      {flow.offer_panel_desc || REG_FLOW_STRINGS.offer_panel_desc}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      <input
                        type="text"
                        value={offerCodeDraft}
                        onChange={(e) => setOfferCodeDraft(e.target.value.toUpperCase())}
                        placeholder={flow.offer_code_placeholder || REG_FLOW_STRINGS.offer_code_placeholder}
                        style={{
                          flex: '1 1 200px',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: '1px solid var(--a11y-surface-border)',
                          background: 'var(--a11y-input-bg)',
                          color: 'var(--a11y-input-text)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleApplyOfferAndMaybeContinue}
                        disabled={offerValidating || !offerCodeDraft.trim()}
                        className="enter-button"
                        style={{ padding: '12px 20px' }}
                      >
                        {offerValidating ? '…' : (flow.offer_btn_apply || REG_FLOW_STRINGS.offer_btn_apply)}
                      </button>
                      {appliedOffer && (
                        <button
                          type="button"
                          onClick={handleClearOffer}
                          style={{
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '1px solid var(--a11y-surface-border)',
                            background: 'transparent',
                            color: 'var(--a11y-page-text)',
                            cursor: 'pointer',
                          }}
                        >
                          {flow.offer_btn_clear || REG_FLOW_STRINGS.offer_btn_clear}
                        </button>
                      )}
                    </div>
                    {renderAppliedOfferFeedback()}
                    {offerError && (
                      <p style={{ color: '#f87171', fontSize: '0.85rem', marginTop: '10px', marginBottom: 0 }}>
                        {offerError}
                      </p>
                    )}
                  </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="fade-in">
                  <header style={{ marginBottom: '40px', borderBottom: '1px solid var(--reg-glass-border-subtle)', paddingBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h2 style={{ fontWeight: '700', color: '#138a8a', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>{sections[currentStep-1].label}</h2>
                        <span style={{ fontSize: '0.8rem', opacity: 0.45, color: 'var(--a11y-page-text)' }}>{currentStep} / {sections.length}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.65, fontWeight: '300', color: 'var(--a11y-page-text)' }}>{t.reg_ui.intro}</p>
                  </header>
                  <div className="reg-form-questions-grid">
                    {(() => {
                      const entries = Object.entries(sections[currentStep-1].questions);
                      const orderedEntries = currentStep === 1 ? [
                        ...entries.filter(([k]) => k === 'officialName'),
                        ...entries.filter(([k]) => k === 'officialAddress'),
                        ...(formData.answers?.officialAddressCountry ? [] : entries.filter(([k]) => k === 'officialAddressCountry')),
                        ...entries.filter(([k]) => !['officialName', 'officialAddress', 'officialAddressCountry'].includes(k))
                      ] : entries;
                      return orderedEntries.map(([qKey, qMetadata]) => {
                        const isFullWidth = qMetadata.type === 'textarea' || qMetadata.type === 'checkbox' || qMetadata.type === 'boolean' || qMetadata.type === 'percentageGrid' || qMetadata.type === 'exclusionsGrid';
                        return (
                       <div key={qKey} className={isFullWidth ? 'reg-form-field--full' : undefined}>
                    <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.95rem', fontWeight: '600', color: 'var(--a11y-page-text)' }}>{qMetadata.label}</label>
                        {qMetadata.description && qMetadata.type !== 'exclusionsGrid' && (
                          <p style={{ marginTop: '-6px', marginBottom: '15px', fontSize: '0.85rem', opacity: 0.55, lineHeight: '1.5', color: 'var(--a11y-page-text)' }}>
                            {qMetadata.description}
                          </p>
                        )}
                        {qMetadata.type === 'exclusionsGrid' && qMetadata.description && (
                          <p className="reg-exclusions-grid-instruction">{qMetadata.description}</p>
                        )}
                        {renderInput(qKey, qMetadata)}
                      </div>
                    );
                  });
                  })()}
                  </div>
                </div>
              )}

             {!resumptionStatus && (
                <>
                  <div ref={sentinelRef} style={{ height: '10px', margin: '30px 0' }} />

                  <footer className="reg-form-footer">
                    <button
                      type="button"
                      onClick={() => {
                        if (currentStep === 1) setStep0Screen('select');
                        setCurrentStep(currentStep - 1);
                      }}
                      disabled={currentStep === 0}
                      style={{ visibility: currentStep === 0 ? 'hidden' : 'visible', padding: '12px 30px', borderRadius: '8px', background: 'transparent', color: 'var(--a11y-page-text)', border: '1px solid var(--reg-glass-border-subtle)', cursor: 'pointer' }}
                    >
                      {t.reg_ui.btn_back}
                    </button>

                    {currentStep < totalSteps - 1 ? (
                      (currentStep !== 0 || step0Screen === 'select') ? (
                        <button type="button" onClick={handleNextStep} disabled={!canProgress || isSubmitting}
                          style={{ padding: '12px 40px', borderRadius: '8px', background: (canProgress && !isSubmitting) ? '#138a8a' : 'var(--reg-glass-bg-soft)', color: (canProgress && !isSubmitting) ? 'var(--a11y-btn-text)' : 'var(--reg-disabled-btn-fg)', border: 'none', cursor: (canProgress && !isSubmitting) ? 'pointer' : 'not-allowed', transition: '0.3s', fontWeight: 'bold' }}
                        >
                          {isSubmitting ? "..." : (currentStep === 0 ? (flow.step0_btn_continue || REG_FLOW_STRINGS.step0_btn_continue) : t.reg_ui.btn_next)}
                        </button>
                      ) : null
                    ) : (
                      <button type="submit" disabled={isSubmitting || !canProgress || !isFinalSectionValid()} 
                        style={{ padding: '12px 40px', borderRadius: '8px', background: (canProgress && isFinalSectionValid()) ? '#138a8a' : 'var(--reg-glass-bg-soft)', color: (canProgress && isFinalSectionValid()) ? 'var(--a11y-btn-text)' : 'var(--reg-disabled-btn-fg)', border: 'none', cursor: (canProgress && isFinalSectionValid()) ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}
                      >
                        {isSubmitting ? t.reg_ui.status_submitting : t.reg_ui.btn_submit}
                      </button>
                    )}
                  </footer>
                </>
              )}
            </form>
          </main>
        </>
      )}

      <footer style={{ paddingBottom: '40px', textAlign: 'center', fontSize: '0.6rem', opacity: 0.35, letterSpacing: '2px', color: 'var(--a11y-page-text)' }}>
        © {new Date().getFullYear()} IFIP
      </footer>
      <SupportIssueModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        sourceSurface="registration"
        context={supportContext}
      />
    </div>
    </>
  );
};

export default Register;